import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  Box,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  LinearProgress,
  Avatar,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Alert,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { formatCreditAmount } from '../utils/format';
import UpgradePlanModal from './UpgradePlanModal';
import PayAsYouNeedModal from './PayAsYouNeedModal';
import WorkspacesTab from './WorkspacesTab';
import { useWithTokenRefresh } from '../utils/api';
import { useApi } from '../contexts/ApiContext';
import { getPlanTier, getPlanColor } from '../utils/subscriptionUtils';

import { SubscriptionTypes, QuotaTypes, OrganizationTypes, SystemEventTypes } from '@flashbacktech/flashbackclient/api';
import { usePermissions } from '../hooks/usePermissions';

interface OrganizationModalProps {
  open: boolean;
  onClose: () => void;
  initialSection?: 'Details' | 'Plan' | 'Billing' | 'Activity';
  initialShowHover?: boolean;
}

const SidebarItem = styled(ListItem)(() => ({
  padding: '8px 12px',
  borderRadius: '6px',
  width: '136px',
  height: '36px',
  gap: '8px',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    cursor: 'pointer',
  },
}));

const SidebarItemActive = styled(ListItem)(() => ({
  padding: '8px 12px',
  borderRadius: '6px',
  width: '136px',
  height: '36px',
  gap: '8px',
  backgroundColor: '#FAFAFA',
  color: '#414651',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: '#FAFAFA',
    cursor: 'pointer',
  },
}));

const OrganizationModal: React.FC<OrganizationModalProps> = ({
  open,
  onClose,
  initialSection,
  initialShowHover,
}) => {
  const { authState, globalData, refreshGlobalData } = useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [org, setOrg] = React.useState<{
    id: string;
    name: string;
    domain: string;
    address1?: string;
  } | null>(null);
  const [team, setTeam] = React.useState<
    OrganizationTypes.OrgUserResponse[]
  >([]);
  const [activeSection, setActiveSection] = React.useState<'Details' | 'Plan' | 'Billing' | 'Activity'>(
    initialSection || 'Details'
  );
  const [activeTab, setActiveTab] = React.useState<'Organization' | 'Team' | 'Workspace'>(
    'Organization'
  );
  const [addingMethod, setAddingMethod] = React.useState<boolean>(false);
  const [upgradePlanModalOpen, setUpgradePlanModalOpen] = React.useState(false);
  const [payAsYouNeedOpen, setPayAsYouNeedOpen] = React.useState(false);
  // Hover details for Subscription card
  const [showSubDetails, setShowSubDetails] = React.useState(initialShowHover || false);
  const subCardRef = React.useRef<HTMLDivElement | null>(null);
  const [subPanelPos, setSubPanelPos] = React.useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  // États pour les compteurs de ressources
  const [bucketsCount, setBucketsCount] = React.useState(0);
  const [repositoriesCount, setRepositoriesCount] = React.useState(0);

  // États pour les subscriptions
  const [subscriptions, setSubscriptions] = React.useState<
    SubscriptionTypes.SubscriptionResponse[]
  >([]);
  const [currentSubscription, setCurrentSubscription] =
    React.useState<SubscriptionTypes.OrgSubscriptionResponse | null>(null);
  const [payments, setPayments] = React.useState<SubscriptionTypes.PaymentResponse[]>([]);
  const [subscriptionLoading, setSubscriptionLoading] = React.useState(false);
  const [paymentsLoading, setPaymentsLoading] = React.useState(false);
  const [selectedPeriodType, setSelectedPeriodType] = React.useState<QuotaTypes.PeriodType>(
    QuotaTypes.PeriodType.MONTHLY
  );

  // Credits-based billing (when backend SUBSCRIPTION_SYSTEM=credits)
  const [creditsMode, setCreditsMode] = React.useState(false);
  const [creditsBalance, setCreditsBalance] = React.useState<{
    realtimeBalance: number;
    creditBalance: number;
    pendingConsumption: number;
    breakdown: { storage: number; ai: number };
  } | null>(null);
  const [creditPacks, setCreditPacks] = React.useState<{ id: string; name: string; code: string; credits: number; price: number }[]>([]);
  const [creditTransactions, setCreditTransactions] = React.useState<Array<{
    id: string;
    creditAmount: number;
    createdAt: string;
    description: string | null;
    type: string;
    subscriptionName?: string;
    packName?: string;
  }>>([]);
  const [creditsLoading, setCreditsLoading] = React.useState(false);

  // Activity section states
  const { isOrgManager } = usePermissions();
  const currentUserId = globalData?.userProfile?.id || '';
  const [activityEvents, setActivityEvents] = React.useState<SystemEventTypes.SystemEventResponse[]>([]);
  const [activityLoading, setActivityLoading] = React.useState(false);
  const [activitySearch, setActivitySearch] = React.useState('');
  const [activityWorkspaceFilter, setActivityWorkspaceFilter] = React.useState<string>('all');
  const [activityUserFilter, setActivityUserFilter] = React.useState<string>('all');

  // État pour les quotas depuis /user/quota
  const [userQuota, setUserQuota] = React.useState<{
    subscription: any;
    quotaUsage: Array<{
      capability: {
        id: string;
        code: string;
        description: string;
        type: string;
        periodType: string;
      };
      usage: {
        current: number;
        max: number;
        percentage: number;
      };
    }>;
  } | null>(null);

  const defaultQuotaData = {
    storageUsed: 0,
    storageLimit: 0,
    storagePercentage: 0,
    subscriptionName: '',
    subscriptionType: '',
    displayLimit: '',
  };

  // État pour stocker les données de quota
  const [quotaData, setQuotaData] = React.useState<{
    storageUsed: number;
    storageLimit: number;
    storagePercentage: number;
    subscriptionName: string;
    subscriptionType: string;
    displayLimit: string;
  }>(() => {
    const savedQuota = localStorage.getItem('quotaData');
    if (savedQuota) {
      try {
        const parsed = JSON.parse(savedQuota);
        if (parsed && typeof parsed === 'object') {
          return {
            ...defaultQuotaData,
            ...parsed,
            subscriptionName: typeof (parsed as { subscriptionName?: unknown }).subscriptionName === 'string' ? (parsed as { subscriptionName: string }).subscriptionName : '',
            subscriptionType: typeof (parsed as { subscriptionType?: unknown }).subscriptionType === 'string' ? (parsed as { subscriptionType: string }).subscriptionType : '',
            displayLimit: typeof (parsed as { displayLimit?: unknown }).displayLimit === 'string' ? (parsed as { displayLimit: string }).displayLimit : '',
          };
        }
      } catch (error) { /* Ignore errors */ }
    }
    return defaultQuotaData;
  });
  // Organization (company) edit state
  const [companyLoading, setCompanyLoading] = React.useState(false);
  const [companyError, setCompanyError] = React.useState<string | null>(null);
  const [companyName, setCompanyName] = React.useState<string>('');
  const [companyDomain, setCompanyDomain] = React.useState<string>('');
  const [companyAddress1, setCompanyAddress1] = React.useState<string>('');
  const [companyAddress2, setCompanyAddress2] = React.useState<string>('');
  const [companyCity, setCompanyCity] = React.useState<string>('');
  const [companyZipcode, setCompanyZipcode] = React.useState<string>('');
  const [companyState, setCompanyState] = React.useState<string>('');
  const [companyCountry, setCompanyCountry] = React.useState<string>('');
  const [companyPhone, setCompanyPhone] = React.useState<string>('');
  const [phoneCountryCode, setPhoneCountryCode] = React.useState<string>('+1');
  const [companyWebsite, setCompanyWebsite] = React.useState<string>('');
  const [companyMfaEnforced, setCompanyMfaEnforced] = React.useState<boolean>(false);
  const [canEditCompany, setCanEditCompany] = React.useState<boolean>(false);

  // Team management state (parity with ProfileModal)
  const [teamLoading, setTeamLoading] = React.useState(false);
  const [teamError, setTeamError] = React.useState<string>('');
  const [editingMemberId, setEditingMemberId] = React.useState<string | null>(null);
  const [editingRole, setEditingRole] = React.useState<number>(0);

  // Workspace unsaved changes tracking
  const [workspaceHasChanges, setWorkspaceHasChanges] = React.useState(false);
  const [workspaceWarnDialog, setWorkspaceWarnDialog] = React.useState(false);
  const [workspacePendingAction, setWorkspacePendingAction] = React.useState<(() => void) | null>(null);
  const [addMemberOpen, setAddMemberOpen] = React.useState(false);
  const [canManageUsers, setCanManageUsers] = React.useState(true);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteFirstName, setInviteFirstName] = React.useState('');
  const [inviteLastName, setInviteLastName] = React.useState('');
  const [invitePassword, setInvitePassword] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState<number>(0);

  // Helper to always get the latest token
  const getAuthToken = React.useCallback(() => {
    if (authState?.token) return authState.token;
    try {
      const saved = localStorage.getItem('authState');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed?.token || null;
      }
    } catch (_e) {
      return null;
    }
    return null;
  }, [authState?.token]);

  // Fonction pour charger le nombre total de buckets (workspace ALL)
  const loadBucketsCount = React.useCallback(async () => {
    try {
      if (!authState.token) return;
      const response = await withTokenRefresh(apiClient.getStorageBuckets.bind(apiClient));
      if (response && response.buckets && Array.isArray(response.buckets)) {
        // Toujours compter TOUS les buckets (workspace ALL)
        setBucketsCount(response.buckets.length);
      } else {
        setBucketsCount(0);
      }
    } catch (error) {
      setBucketsCount(0);
    }
  }, [authState.token, withTokenRefresh, apiClient]);

  // Fonction pour charger le nombre total de repositories (workspace ALL)
  const loadRepositoriesCount = React.useCallback(async () => {
    try {
      if (!authState.token) return;
      const response = await withTokenRefresh(apiClient.getStorageRepos.bind(apiClient));
      if (response && response.repos && Array.isArray(response.repos)) {
        // Toujours compter TOUS les repos (workspace ALL)
        setRepositoriesCount(response.repos.length);
      } else {
        setRepositoriesCount(0);
      }
    } catch (error) {
      setRepositoriesCount(0);
    }
  }, [authState.token, withTokenRefresh, apiClient]);

  // Fonction pour charger les données de quota
  const loadQuota = React.useCallback(async () => {
    try {
      if (!authState.token) {
        return;
      }

      const response = await withTokenRefresh(apiClient.getUserQuota.bind(apiClient));

      if (
        response &&
        response.success &&
        response.quotaUsage &&
        Array.isArray(response.quotaUsage)
      ) {
        // Chercher la capacité de trafic (traffic.max) dans les données de quota
        const trafficQuota = response.quotaUsage.find(
          (item: any) => item.capability && item.capability.code === 'traffic.max'
        );

        if (trafficQuota && trafficQuota.usage) {
          // Convertir les valeurs de MB à GB
          const current = trafficQuota.usage.current / 1024;
          const max = trafficQuota.usage.max / 1024;

          // Formater la limite pour l'affichage
          let displayLimit: string;
          if (max >= 1024) {
            // Convertir en TB si c'est plus de 1024 GB
            displayLimit = (max / 1024).toFixed(1) + ' TB';
          } else {
            displayLimit = max.toFixed(1) + ' GB';
          }

          // Utiliser le pourcentage fourni par l'API, ou le calculer si nécessaire
          const percentage = trafficQuota.usage.percentage || (max > 0 ? (current / max) * 100 : 0);

          // Déterminer le type d'abonnement (Business ou Individual)
          let subscriptionType = '';
          if (response.subscription && response.subscription.name) {
            subscriptionType = response.subscription.name;
          }

          // Mettre à jour l'état
          const newQuotaData = {
            storageUsed: current,
            storageLimit: max,
            storagePercentage: percentage,
            subscriptionName: response.subscription?.name || '',
            subscriptionType: subscriptionType,
            displayLimit,
          };

          setQuotaData(newQuotaData);
        }
      }
    } catch (error) { /* Ignore errors */ }
  }, [authState.token, withTokenRefresh, apiClient]);

  React.useEffect(() => {
    if (open && authState.token) {
      loadQuota();
      loadBucketsCount();
      loadRepositoriesCount();
    }
  }, [open, authState.token, loadQuota, loadBucketsCount, loadRepositoriesCount]);

  const updateSubPanelPosition = React.useCallback(() => {
    const el = subCardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setSubPanelPos({
      top: Math.max(0, Math.round(rect.top - 10)),
      left: Math.round(rect.right + 16),
    });
  }, []);

  React.useEffect(() => {
    if (!showSubDetails) return;
    updateSubPanelPosition();
    const onResize = () => updateSubPanelPosition();
    const onScroll = () => updateSubPanelPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showSubDetails, updateSubPanelPosition]);

  // Update section and hover state when modal opens with initial values
  React.useEffect(() => {
    if (open) {
      if (initialSection) {
        setActiveSection(initialSection);
      }
      if (initialShowHover !== undefined && initialShowHover) {
        setShowSubDetails(true);
        // Use requestAnimationFrame to ensure positioning happens after DOM is fully rendered
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            updateSubPanelPosition();
          });
        });
      }
    }
  }, [open, initialSection, initialShowHover, updateSubPanelPosition]);

  // Fonction réutilisable pour charger les quotas utilisateur
  const loadUserQuota = React.useCallback(async () => {
    if (!authState?.token) return;
    try {
      const quotaResp = await withTokenRefresh(() => apiClient.getUserQuota());
      if (quotaResp.success) {
        setUserQuota({
          subscription: quotaResp.subscription,
          quotaUsage: quotaResp.quotaUsage || [],
        });
      }
    } catch (error) { /* Ignore errors */ }
  }, [authState?.token, withTokenRefresh, apiClient]);

  // Fonction réutilisable pour charger les subscriptions
  const loadSubscriptionsData = React.useCallback(async () => {
    if (!authState?.token) return;
    try {
      setSubscriptionLoading(true);
      const subsResp = await withTokenRefresh(() => apiClient.getSubscriptions());
      if (subsResp.success) {
        setSubscriptions(subsResp.data || []);
      }
      // Get current subscription
      const mySubResp = await withTokenRefresh(() => apiClient.getMySubscription());
      if (mySubResp.success) {
        setCurrentSubscription(mySubResp.data);
        // Set the selected period type to match the current subscription
        if (mySubResp.data?.periodType) {
          setSelectedPeriodType(mySubResp.data.periodType as QuotaTypes.PeriodType);
        }
      }
    } catch (error) { /* Ignore errors */ } finally {
      setSubscriptionLoading(false);
    }
  }, [authState?.token, withTokenRefresh, apiClient]);

  // Load user quota and subscriptions when Plan section is active
  React.useEffect(() => {
    if (open && activeSection === 'Plan') {
      loadUserQuota();
      loadSubscriptionsData();
    }
  }, [open, activeSection, loadUserQuota, loadSubscriptionsData]);

  // Load credits data when Plan or Billing is active (credits-based billing)
  const loadCreditsData = React.useCallback(async () => {
    if (!authState?.token) return;
    try {
      const balanceResp = await withTokenRefresh(() => (apiClient as any).getCreditsBalance()) as { success: boolean; data?: { realtimeBalance: number; creditBalance: number; pendingConsumption: number; breakdown: { storage: number; ai: number } } };
      if (!balanceResp.success || !balanceResp.data) {
        setCreditsMode(false);
        return;
      }
      setCreditsMode(true);
      setCreditsBalance({
        realtimeBalance: balanceResp.data.realtimeBalance,
        creditBalance: balanceResp.data.creditBalance,
        pendingConsumption: balanceResp.data.pendingConsumption,
        breakdown: balanceResp.data.breakdown,
      });
      const [packsResp, txResp] = await Promise.all([
        withTokenRefresh(() => (apiClient as any).getCreditsPacks()) as Promise<{ success: boolean; data?: { id: string; name: string; code: string; credits: number; price: number }[] }>,
        withTokenRefresh(() => (apiClient as any).getCreditsTransactions({ limit: 20, page: 1 })) as Promise<{ success: boolean; data?: Array<{ id: string; creditAmount: number; createdAt: string; description: string | null; type: string }> }>,
      ]);
      if (packsResp.success && packsResp.data) setCreditPacks(packsResp.data);
      if (txResp.success && txResp.data) setCreditTransactions(txResp.data);
    } catch {
      setCreditsMode(false);
    }
  }, [authState?.token, withTokenRefresh, apiClient]);

  React.useEffect(() => {
    if (open && (activeSection === 'Plan' || activeSection === 'Billing')) {
      setCreditsLoading(true);
      loadCreditsData().finally(() => setCreditsLoading(false));
    }
  }, [open, activeSection, loadCreditsData]);

  // Load activity events when Activity section is active
  const loadActivityEvents = React.useCallback(async () => {
    if (!authState?.token) return;
    setActivityLoading(true);
    try {
      const data: SystemEventTypes.SystemEventQueryRequest = {
        skip: 0,
        take: 100,
      };
      const res = await withTokenRefresh(() => apiClient.getSystemEvents(data));
      if (res && Array.isArray(res.events)) {
        setActivityEvents(res.events);
      }
    } catch { /* ignore */ } finally {
      setActivityLoading(false);
    }
  }, [authState?.token, withTokenRefresh, apiClient]);

  React.useEffect(() => {
    if (open && activeSection === 'Activity') {
      loadActivityEvents();
    }
  }, [open, activeSection, loadActivityEvents]);

  const handleBuyCreditsPack = React.useCallback(async (packId: string) => {
    try {
      const response = await withTokenRefresh(() => (apiClient as any).buyCreditsPack({ packId })) as { success: boolean; checkoutUrl?: string | null; message?: string };
      if (response.success && response.checkoutUrl) {
        window.open(response.checkoutUrl, '_blank', 'noopener,noreferrer');
      } else if (response.success) {
        await loadCreditsData();
      } else {
        alert(response.message || 'Failed to start checkout.');
      }
    } catch (e: any) {
      alert(e?.message || 'Failed to start checkout.');
    }
  }, [withTokenRefresh, apiClient, loadCreditsData]);

  // Load payments when Billing section is active
  React.useEffect(() => {
    let mounted = true;
    const loadPaymentsData = async () => {
      if (!open || activeSection !== 'Billing' || !authState?.token) return;
      try {
        setPaymentsLoading(true);
        const paymentsResp = await withTokenRefresh(() => apiClient.getPayments());
        if (mounted && paymentsResp.success) {
          setPayments(paymentsResp.data || []);
        }
      } catch (error) { /* Ignore errors */ } finally {
        if (mounted) setPaymentsLoading(false);
      }
    };
    loadPaymentsData();
    return () => {
      mounted = false;
    };
  }, [open, activeSection, authState?.token, withTokenRefresh, getAuthToken]);

  // Handle subscription purchase
  const handleBuySubscription = async (periodId: string) => {
    try {
      setSubscriptionLoading(true);
      const response = await withTokenRefresh(() =>
        apiClient.buySubscription({ subscriptionPeriodId: periodId })
      );

      if (response.success) {
        if (response.checkoutUrl) {
          // Paid subscription - open Stripe Checkout in a new window
          window.open(response.checkoutUrl, '_blank', 'noopener,noreferrer');
        } else {
          // Free subscription - checkoutUrl is undefined, refresh subscription status
          await loadSubscriptionsData();
        }
      } else {
        alert('Failed to process subscription. Please try again.');
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      
      // Handle specific error cases
      if (error?.status === 409) {
        alert('You already have an active subscription. Please cancel your current subscription before upgrading to a new plan.');
      } else if (error?.data?.message) {
        alert(error.data.message);
      } else if (error?.message) {
        alert(error.message);
      } else {
        alert('Failed to start checkout. Please try again or contact support.');
      }
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Handle subscription cancellation
  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    try {
      setSubscriptionLoading(true);
      const response = await withTokenRefresh(() => apiClient.cancelSubscription());
      if (response.success) {
        await loadSubscriptionsData();
        await loadUserQuota();
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to cancel subscription');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Handle pending payment cancellation
  const handleCancelPendingPayment = async () => {
    if (!window.confirm('Are you sure you want to cancel this pending payment?')) return;
    try {
      setPaymentsLoading(true);
      const response = await withTokenRefresh(() => apiClient.cancelPendingPayment());
      if (response.success) {
        // Reload payments to reflect the change
        const paymentsResp = await withTokenRefresh(() => apiClient.getPayments());
        if (paymentsResp.success) {
          setPayments(paymentsResp.data || []);
        }
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to cancel pending payment');
    } finally {
      setPaymentsLoading(false);
    }
  };

  // Handle navigate to checkout for pending payment
  const handleNavigateToCheckout = async () => {
    try {
      setPaymentsLoading(true);
      const response = await withTokenRefresh(() => apiClient.getPendingPayment());
      if (response.success && response.data?.checkoutUrl) {
        // Navigate to the checkout URL
        window.open(response.data.checkoutUrl, '_blank', 'noopener,noreferrer');
      } else {
        alert('Failed to get checkout URL. Please try again.');
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to get checkout URL');
    } finally {
      setPaymentsLoading(false);
    }
  };

  // Open Stripe Billing Portal
  const handleOpenBillingPortal = async () => {
    try {
      const response = await withTokenRefresh(() => apiClient.createBillingPortal());
      if (response.success && response.url) {
        window.open(response.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to open billing portal');
    }
  };

  // Helper functions for display
  const formatPeriodType = (type: QuotaTypes.PeriodType): string => {
    switch (type) {
      case QuotaTypes.PeriodType.MONTHLY:
        return 'Monthly';
      case QuotaTypes.PeriodType.YEARLY:
        return 'Yearly';
      case QuotaTypes.PeriodType.WEEKLY:
        return 'Weekly';
      case QuotaTypes.PeriodType.DAILY:
        return 'Daily';
      case QuotaTypes.PeriodType.ALL_TIME:
        return 'Lifetime';
      default:
        return type;
    }
  };

  // Format bytes to human-readable format (TB/GB)
  const formatBytes = (bytes: number): string => {
    const tb = bytes / (1024 * 1024 * 1024 * 1024);
    if (tb >= 1) {
      return `${tb.toFixed(1)} TB`;
    }
    const gb = bytes / (1024 * 1024 * 1024);
    return `${gb.toFixed(0)} GB`;
  };

  // Helper functions to get quota by capability code
  const getQuotaByCode = (code: string) => {
    return userQuota?.quotaUsage?.find((q) => q.capability.code === code);
  };

  const formatPrice = (price: number, currency = 'USD'): string => {
    const symbol = currency.toUpperCase() === 'USD' ? '$' : currency;
    return `${symbol}${price}`;
  };

  const formatPaymentStatus = (status: string) => {
    const config: Record<string, { label: string; bgcolor: string; color: string }> = {
      COMPLETED: { label: 'Paid', bgcolor: '#EAF8F1', color: '#1F8F6B' },
      FAILED: { label: 'Failed', bgcolor: '#FEE2E2', color: '#EF4444' },
      EXPIRED: { label: 'Expired', bgcolor: '#FEF3C7', color: '#F59E0B' },
      CANCELLED: { label: 'Cancelled', bgcolor: '#F3F4F6', color: '#6B7280' },
      PENDING: { label: 'Pending', bgcolor: '#FFF7E6', color: '#C38A00' },
    };
    return config[status] || config['PENDING'];
  };

  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!open) return;
      setLoading(true);
      setError(null);
      try {
        // Use globalData.userProfile instead of API call
        const orgId = globalData?.userProfile?.orgId;
        
        if (!orgId) {
          setError('No organization found for this account.');
          setOrg(null);
          setTeam([]);
          return;
        }
        
        // Note: orgRole is not in globalData, but we can fetch it if needed
        // For now, we'll skip the role check or fetch it separately if required
        // setCanEditCompany(orgRole === 0xfe || orgRole === 0xff);

        // Fetch organization
        const orgResp = await withTokenRefresh(() => apiClient.getOrganization(orgId));
        if (orgResp.success && orgResp.data) {
          const d = orgResp.data as any;
          setOrg({
            id: orgId,
            name: d.name || '',
            domain: d.domain || '',
            address1: d.address1 || '',
          });
          setCompanyName(d.name || '');
          setCompanyDomain(d.domain || '');
          setCompanyAddress1(d.address1 || '');
          setCompanyAddress2(d.address2 || '');
          setCompanyCity(d.city || '');
          setCompanyZipcode(d.zipcode || '');
          setCompanyState(d.state || '');
          setCompanyCountry(d.country || '');
          setCompanyWebsite(d.website || '');
          setCompanyMfaEnforced(d.mfaEnforced === true);
          // Parse phone: extract country code if present
          if (d.phone) {
            const phoneMatch = d.phone.match(/^(\+\d{1,4})(.*)/);
            if (phoneMatch) {
              setPhoneCountryCode(phoneMatch[1]);
              setCompanyPhone(phoneMatch[2]);
            } else {
              setCompanyPhone(d.phone);
            }
          }
        }

      } catch (e: any) {
        if (mounted) setError(e?.message || 'Failed to load organization');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [open, globalData?.userProfile?.orgId, authState?.token, withTokenRefresh, apiClient]);

  const handleSaveCompany = async () => {
    if (!org?.id || !canEditCompany) return;
    setCompanyLoading(true);
    setCompanyError(null);
    try {
      const fullPhone = companyPhone.trim() ? `${phoneCountryCode}${companyPhone.trim()}` : undefined;
      const body: any = {
        name: companyName.trim() || undefined,
        domain: companyDomain.trim() || undefined,
        address1: companyAddress1.trim() || undefined,
        address2: companyAddress2.trim() || undefined,
        city: companyCity.trim() || undefined,
        zipcode: companyZipcode.trim() || undefined,
        state: companyState.trim() || undefined,
        country: companyCountry.trim() || undefined,
        phone: fullPhone,
        website: companyWebsite.trim() || undefined,
        mfaEnforced: companyMfaEnforced,
      };
      const resp = await withTokenRefresh(() => apiClient.updateOrganization(org.id, body));
      if (resp.success) {
        // Refresh org data from response
        if (resp.data) {
          const d = resp.data as any;
          setCompanyName(d.name || '');
          setCompanyDomain(d.domain || '');
          setCompanyAddress1(d.address1 || '');
          setCompanyAddress2(d.address2 || '');
          setCompanyCity(d.city || '');
          setCompanyZipcode(d.zipcode || '');
          setCompanyState(d.state || '');
          setCompanyCountry(d.country || '');
          setCompanyWebsite(d.website || '');
          setCompanyMfaEnforced(d.mfaEnforced === true);
          if (d.phone) {
            const phoneMatch = d.phone.match(/^(\+\d{1,4})(.*)/);
            if (phoneMatch) {
              setPhoneCountryCode(phoneMatch[1]);
              setCompanyPhone(phoneMatch[2]);
            } else {
              setCompanyPhone(d.phone);
            }
          }
        }
        await refreshGlobalData(true);
      } else {
        throw new Error(resp.message || 'Failed to update organization');
      }
    } catch (e: any) {
      setCompanyError(e?.message || 'Failed to update organization');
    } finally {
      setCompanyLoading(false);
    }
  };

  // Helper to load users with their roles
  const loadUsersWithRoles = async () => {
    const res = await withTokenRefresh(() => apiClient.getOrganizationUsers());
    if (res?.success && Array.isArray(res.data)) {
      return res.data;
    }
    return [];
  };

  // Lazy-load team when switching to Team tab
  React.useEffect(() => {
    let mounted = true;
    const loadTeam = async () => {
      if (!open || activeTab !== 'Team' || !org) return;
      try {
        setTeamError('');
        setTeamLoading(true);
        const usersWithRoles = await loadUsersWithRoles();
        if (!mounted) return;
        if (usersWithRoles.length > 0) {
          setTeam(usersWithRoles);
          setCanManageUsers(true);
        } else {
          setTeamError('No users found or failed to load team');
        }
      } catch (e: any) {
        if (!mounted) return;
        if (e?.status === 403) {
          setCanManageUsers(false);
          setTeamError(
            'Insufficient permissions to view team members (requires WORKSPACES role or higher).'
          );
        } else {
          setTeamError(e?.message || 'Failed to load team');
        }
      } finally {
        if (mounted) setTeamLoading(false);
      }
    };
    loadTeam();
    return () => {
      mounted = false;
    };
  }, [activeTab, open, org, withTokenRefresh, getAuthToken]);

  const handleInvite = async () => {
    if (!inviteEmail || !inviteFirstName || !inviteLastName || !invitePassword || !org?.id) return;
    try {
      setTeamLoading(true);
      const res = await withTokenRefresh(() =>
        apiClient.createOrganizationUser({
          email: inviteEmail,
          password: invitePassword,
          firstName: inviteFirstName || '',
          lastName: inviteLastName || '',
          orgRole: inviteRole || 0,
        })
      );
      if (res.success) {
        setInviteEmail('');
        setInviteFirstName('');
        setInviteLastName('');
        setInvitePassword('');
        setInviteRole(0);
        setInviteOpen(false);
        // reload
        const users = await loadUsersWithRoles();
        setTeam(users);
      } else {
        setTeamError(res?.message || 'Failed to invite user');
      }
    } catch (e: any) {
      setTeamError(e?.message || 'Failed to invite user');
    } finally {
      setTeamLoading(false);
    }
  };

  // Get role names from bitfield - roles work as flags, not single values
  const getRoleName = (role: number): string => {
    // Special cases: OWNER and ADMINISTRATOR are exclusive
    if (role === 255) return 'OWNER (0xff)';
    if (role === 254) return 'ADMINISTRATOR (0xfe)';
    
    // If role is 0, it's just USER
    if (role === 0) return 'USER (0x00)';
    
    // Decode bitfield: check which role bits are set
    const roles: string[] = [];
    
    if (role & 0x01) roles.push('BILLING (0x01)');
    if (role & 0x02) roles.push('WORKSPACES (0x02)');
    
    // If no standard roles found but value is non-zero, return with hex value
    if (roles.length === 0) return `UNKNOWN (0x${role.toString(16).padStart(2, '0')})`;
    
    return roles.join(', ');
  };

  const handleEditRole = (memberId: string, currentRole: number) => {
    setEditingMemberId(memberId);
    setEditingRole(currentRole);
  };

  const handleSaveRole = async (userId: string) => {
    try {
      setTeamLoading(true);
      // TODO: Appeler l'API pour mettre à jour le rôle
      // await OrganizationService.updateUserRole(userId, editingRole);
      
      // Mise à jour locale en attendant l'API
      setTeam(prevTeam =>
        prevTeam.map(member =>
          member.id === userId ? { ...member, role: editingRole } : member
        )
      );
      setEditingMemberId(null);
    } catch (error: any) {
      setTeamError(error?.message || 'Failed to update role');
    } finally {
      setTeamLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingMemberId(null);
    setEditingRole(0);
  };

  const handleDelete = async (userId: string) => {
    try {
      setTeamLoading(true);
      const res = await withTokenRefresh(() =>
        apiClient.deleteOrganizationUser(userId)
      );
      if (res?.success) {
        const users = await loadUsersWithRoles();
        setTeam(users);
      } else {
        setTeamError(res?.message || 'Failed to remove user');
      }
    } catch (e: any) {
      setTeamError(e?.message || 'Failed to remove user');
    } finally {
      setTeamLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: { xs: '0', sm: '16px' },
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            height: { xs: '100vh', sm: '720px' },
            width: { xs: '100vw', sm: '800px' },
            maxWidth: '100%',
            m: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              height: '100%',
              minHeight: 0,
            }}
          >
            {/* Sidebar */}
            <Box
              sx={{
                width: { xs: '100%', sm: '184px' },
                borderRight: { xs: 'none', sm: '1px solid #E9EAEB' },
                borderBottom: { xs: '1px solid #E9EAEB', sm: 'none' },
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'column' },
                justifyContent: 'space-between',
                overflowX: { xs: 'auto', sm: 'visible' },
                py: { xs: 1, sm: 0 },
              }}
            >
              <Box>
                <Box sx={{ height: '10px' }} />
                <List sx={{ px: 1.5, pt: 2 }}>
                  {activeSection === 'Details' ? (
                    <SidebarItemActive onClick={() => setActiveSection('Details')}>
                      <ListItemText
                        primary="Details"
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: 600,
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: '#414651',
                          },
                        }}
                      />
                    </SidebarItemActive>
                  ) : (
                    <SidebarItem onClick={() => setActiveSection('Details')}>
                      <ListItemText
                        primary="Details"
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: 600,
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: '#717680',
                          },
                        }}
                      />
                    </SidebarItem>
                  )}

                  {activeSection === 'Plan' ? (
                    <SidebarItemActive onClick={() => setActiveSection('Plan')}>
                      <ListItemText
                        primary="Plan"
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: 600,
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: '#414651',
                          },
                        }}
                      />
                    </SidebarItemActive>
                  ) : (
                    <SidebarItem onClick={() => setActiveSection('Plan')}>
                      <ListItemText
                        primary="Plan"
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: 600,
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: '#717680',
                          },
                        }}
                      />
                    </SidebarItem>
                  )}

                  {activeSection === 'Billing' ? (
                    <SidebarItemActive onClick={() => setActiveSection('Billing')}>
                      <ListItemText
                        primary="Billing"
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: 600,
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: '#414651',
                          },
                        }}
                      />
                    </SidebarItemActive>
                  ) : (
                    <SidebarItem onClick={() => setActiveSection('Billing')}>
                      <ListItemText
                        primary="Billing"
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: 600,
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: '#717680',
                          },
                        }}
                      />
                    </SidebarItem>
                  )}

                  {isOrgManager && (
                    activeSection === 'Activity' ? (
                      <SidebarItemActive onClick={() => setActiveSection('Activity')}>
                        <ListItemText
                          primary="Activity"
                          primaryTypographyProps={{
                            sx: {
                              fontWeight: 600,
                              fontSize: '14px',
                              lineHeight: '20px',
                              color: '#414651',
                            },
                          }}
                        />
                      </SidebarItemActive>
                    ) : (
                      <SidebarItem onClick={() => setActiveSection('Activity')}>
                        <ListItemText
                          primary="Activity"
                          primaryTypographyProps={{
                            sx: {
                              fontWeight: 600,
                              fontSize: '14px',
                              lineHeight: '20px',
                              color: '#717680',
                            },
                          }}
                        />
                      </SidebarItem>
                    )
                  )}
                </List>
              </Box>

              {/* Plan Tier card at the bottom - Caché sur mobile */}
              <Box sx={{ width: '136px', pb: 2, mx: 'auto', display: { xs: 'none', sm: 'block' } }}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: '12px',
                    borderColor: '#E9EAEB',
                    bgcolor: '#FAFAFA',
                    minHeight: '95px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent sx={{ p: 1.25, pb: 0.75, display: 'flex', flexDirection: 'column', flex: 1, '&:last-child': { pb: 0.75 } }}>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: '#717680',
                          mb: 0.5,
                          letterSpacing: 0.2,
                          textTransform: 'uppercase',
                        }}
                      >
                        Current Plan
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {(() => {
                          const subscriptionName =
                            userQuota?.subscription?.name || quotaData?.subscriptionName || '';
                          const planTier = getPlanTier(subscriptionName);
                          const color = getPlanColor(subscriptionName);

                          return (
                            <>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: color,
                                }}
                              />
                              <Chip
                                label={planTier}
                                size="small"
                                sx={{
                                  height: 22,
                                  borderRadius: '999px',
                                  bgcolor: 'rgba(0,0,0,0.04)',
                                  color: '#414651',
                                  border: '1px solid',
                                  borderColor: '#E9EAEB',
                                  px: 1,
                                  fontWeight: 600,
                                  fontSize: '12px',
                                }}
                              />
                            </>
                          );
                        })()}
                      </Box>
                    </Box>
                    {/* View button at bottom right */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', flex: 1 }}>
                      <Button
                        size="small"
                        onClick={() => setActiveSection('Plan')}
                        sx={{
                          textTransform: 'none',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#617BFF',
                          minWidth: 'auto',
                          px: 1,
                          '&:hover': {
                            backgroundColor: 'rgba(97, 123, 255, 0.08)',
                          },
                        }}
                      >
                        View
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>

            {/* Main Content */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 3,
                  borderBottom: '1px solid #E9EAEB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#101828' }}>
                  {activeSection === 'Details' ? 'Organization Details' : activeSection === 'Activity' ? 'Recent Activity' : activeSection}
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{ color: '#6B7280' }}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Tabs for Details section */}
              {activeSection === 'Details' && (
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    height: { xs: 'auto', sm: '32px' },
                    borderBottom: '1px solid #E9EAEB',
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 0 },
                    mt: 2,
                    pb: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      gap: { xs: 3, sm: 6 },
                      width: '100%',
                      flexWrap: { xs: 'nowrap', sm: 'wrap' },
                      overflowX: { xs: 'auto', sm: 'visible' },
                    }}
                  >
                    <Box
                      onClick={() => setActiveTab('Organization')}
                      sx={{
                        width: { xs: 'auto', sm: '90px' },
                        minWidth: { xs: '90px', sm: 'auto' },
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: activeTab === 'Organization' ? '2px solid #617BFF' : 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '14px',
                          lineHeight: '20px',
                          color: activeTab === 'Organization' ? '#617BFF' : '#717680',
                        }}
                      >
                        Organization
                      </Typography>
                    </Box>
                    <Box
                      onClick={() => setActiveTab('Team')}
                      sx={{
                        width: { xs: 'auto', sm: '68px' },
                        minWidth: { xs: '68px', sm: 'auto' },
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: activeTab === 'Team' ? '2px solid #617BFF' : 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '14px',
                          lineHeight: '20px',
                          color: activeTab === 'Team' ? '#617BFF' : '#717680',
                        }}
                      >
                        Team
                      </Typography>
                    </Box>
                    <Box
                      onClick={() => setActiveTab('Workspace')}
                      sx={{
                        width: { xs: 'auto', sm: '90px' },
                        minWidth: { xs: '90px', sm: 'auto' },
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: activeTab === 'Workspace' ? '2px solid #617BFF' : 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '14px',
                          lineHeight: '20px',
                          color: activeTab === 'Workspace' ? '#617BFF' : '#717680',
                        }}
                      >
                        Workspace
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              <Box sx={{ p: 3, pt: 2, flex: 1, overflowY: 'auto' }}>
                {loading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                    }}
                  >
                    <CircularProgress size={22} />
                  </Box>
                ) : error ? (
                  <Typography sx={{ color: '#EF4444', fontSize: '14px' }}>{error}</Typography>
                ) : !org ? (
                  <Typography sx={{ fontSize: '14px', color: '#475467' }}>
                    No organization information available.
                  </Typography>
                ) : (
                  <>
                    {activeSection === 'Details' && activeTab === 'Organization' && (
                      <Box
                        sx={{
                          width: { xs: '100%', sm: '480px' },
                          mx: 'auto',
                          display: 'flex',
                          flexDirection: 'column',
                          overflowY: 'auto',
                          pr: 1,
                          px: { xs: 2, sm: 0 },
                          mt: 3,
                          flex: 1,
                        }}
                      >
                        {companyError && (
                          <Typography sx={{ color: '#ef4444', fontSize: '14px', mb: 1 }}>
                            {companyError}
                          </Typography>
                        )}
                        {companyLoading && <LinearProgress sx={{ mb: 2 }} />}
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5, color: '#717680' }}>
                            Organization Name<span style={{ color: '#617BFF' }}>*</span>
                          </Typography>
                          <TextField
                            fullWidth size="small" value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            disabled={!canEditCompany || companyLoading}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' } }}
                          />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5, color: '#717680' }}>
                            Domain<span style={{ color: '#617BFF' }}>*</span>
                          </Typography>
                          <TextField
                            fullWidth size="small" value={companyDomain}
                            onChange={(e) => setCompanyDomain(e.target.value)}
                            disabled={!canEditCompany || companyLoading}
                            placeholder="acme.com"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' } }}
                          />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5, color: '#717680' }}>
                            Address Line 1<span style={{ color: '#617BFF' }}>*</span>
                          </Typography>
                          <TextField
                            fullWidth size="small" value={companyAddress1}
                            onChange={(e) => setCompanyAddress1(e.target.value)}
                            disabled={!canEditCompany || companyLoading}
                            placeholder="456 Business Ave"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' } }}
                          />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5, color: '#717680' }}>
                            Address Line 2
                          </Typography>
                          <TextField
                            fullWidth size="small" value={companyAddress2}
                            onChange={(e) => setCompanyAddress2(e.target.value)}
                            disabled={!canEditCompany || companyLoading}
                            placeholder="Suite 100"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' } }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5, color: '#717680' }}>
                              City<span style={{ color: '#617BFF' }}>*</span>
                            </Typography>
                            <TextField
                              fullWidth size="small" value={companyCity}
                              onChange={(e) => setCompanyCity(e.target.value)}
                              disabled={!canEditCompany || companyLoading}
                              placeholder="Los Angeles"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' } }}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5, color: '#717680' }}>
                              Zip Code
                            </Typography>
                            <TextField
                              fullWidth size="small" value={companyZipcode}
                              onChange={(e) => setCompanyZipcode(e.target.value)}
                              disabled={!canEditCompany || companyLoading}
                              placeholder="10001"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' } }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5, color: '#717680' }}>
                              State / Province
                            </Typography>
                            <TextField
                              fullWidth size="small" value={companyState}
                              onChange={(e) => setCompanyState(e.target.value)}
                              disabled={!canEditCompany || companyLoading}
                              placeholder="CA"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' } }}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5, color: '#717680' }}>
                              Country<span style={{ color: '#617BFF' }}>*</span>
                            </Typography>
                            <TextField
                              fullWidth size="small" value={companyCountry}
                              onChange={(e) => setCompanyCountry(e.target.value)}
                              disabled={!canEditCompany || companyLoading}
                              placeholder="US"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' } }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5, color: '#717680' }}>
                            Phone
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Select
                              value={phoneCountryCode}
                              onChange={(e) => setPhoneCountryCode(e.target.value)}
                              size="small"
                              disabled={!canEditCompany || companyLoading}
                              sx={{ minWidth: '110px', borderRadius: '8px', height: '40px' }}
                              renderValue={(value) => {
                                const codes = [
                                  { code: '+1', flag: '\u{1F1FA}\u{1F1F8}' }, { code: '+44', flag: '\u{1F1EC}\u{1F1E7}' },
                                  { code: '+33', flag: '\u{1F1EB}\u{1F1F7}' }, { code: '+49', flag: '\u{1F1E9}\u{1F1EA}' },
                                  { code: '+81', flag: '\u{1F1EF}\u{1F1F5}' }, { code: '+91', flag: '\u{1F1EE}\u{1F1F3}' },
                                  { code: '+61', flag: '\u{1F1E6}\u{1F1FA}' }, { code: '+55', flag: '\u{1F1E7}\u{1F1F7}' },
                                ];
                                const item = codes.find(c => c.code === value);
                                return item ? `${item.flag} ${item.code}` : value;
                              }}
                            >
                              {[
                                { code: '+1', country: 'US', flag: '\u{1F1FA}\u{1F1F8}' },
                                { code: '+1', country: 'CA', flag: '\u{1F1E8}\u{1F1E6}' },
                                { code: '+44', country: 'GB', flag: '\u{1F1EC}\u{1F1E7}' },
                                { code: '+33', country: 'FR', flag: '\u{1F1EB}\u{1F1F7}' },
                                { code: '+49', country: 'DE', flag: '\u{1F1E9}\u{1F1EA}' },
                                { code: '+34', country: 'ES', flag: '\u{1F1EA}\u{1F1F8}' },
                                { code: '+39', country: 'IT', flag: '\u{1F1EE}\u{1F1F9}' },
                                { code: '+351', country: 'PT', flag: '\u{1F1F5}\u{1F1F9}' },
                                { code: '+31', country: 'NL', flag: '\u{1F1F3}\u{1F1F1}' },
                                { code: '+41', country: 'CH', flag: '\u{1F1E8}\u{1F1ED}' },
                                { code: '+81', country: 'JP', flag: '\u{1F1EF}\u{1F1F5}' },
                                { code: '+86', country: 'CN', flag: '\u{1F1E8}\u{1F1F3}' },
                                { code: '+91', country: 'IN', flag: '\u{1F1EE}\u{1F1F3}' },
                                { code: '+61', country: 'AU', flag: '\u{1F1E6}\u{1F1FA}' },
                                { code: '+55', country: 'BR', flag: '\u{1F1E7}\u{1F1F7}' },
                                { code: '+971', country: 'AE', flag: '\u{1F1E6}\u{1F1EA}' },
                                { code: '+65', country: 'SG', flag: '\u{1F1F8}\u{1F1EC}' },
                                { code: '+380', country: 'UA', flag: '\u{1F1FA}\u{1F1E6}' },
                                { code: '+7', country: 'RU', flag: '\u{1F1F7}\u{1F1FA}' },
                              ].map((c, idx) => (
                                <MenuItem key={`${c.code}-${c.country}-${idx}`} value={c.code}>
                                  {c.flag} {c.country} ({c.code})
                                </MenuItem>
                              ))}
                            </Select>
                            <TextField
                              fullWidth size="small" value={companyPhone}
                              onChange={(e) => setCompanyPhone(e.target.value)}
                              disabled={!canEditCompany || companyLoading}
                              placeholder="555-0123"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' } }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5, color: '#717680' }}>
                            Website<span style={{ color: '#617BFF' }}>*</span>
                          </Typography>
                          <TextField
                            fullWidth size="small" value={companyWebsite}
                            onChange={(e) => setCompanyWebsite(e.target.value)}
                            disabled={!canEditCompany || companyLoading}
                            placeholder="https://acme.com"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' } }}
                          />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5, color: '#717680' }}>
                            Enforce MFA
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                              onClick={() => { if (canEditCompany && !companyLoading) setCompanyMfaEnforced(!companyMfaEnforced); }}
                              sx={{
                                width: 40, height: 22, borderRadius: '11px',
                                bgcolor: companyMfaEnforced ? '#617BFF' : '#D0D5DD',
                                cursor: canEditCompany ? 'pointer' : 'default',
                                position: 'relative', transition: 'background-color 0.2s',
                                opacity: canEditCompany ? 1 : 0.6,
                              }}
                            >
                              <Box sx={{
                                width: 18, height: 18, borderRadius: '50%', bgcolor: 'white',
                                position: 'absolute', top: 2,
                                left: companyMfaEnforced ? 20 : 2,
                                transition: 'left 0.2s',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                              }} />
                            </Box>
                            <Typography sx={{ fontSize: '13px', color: companyMfaEnforced ? '#617BFF' : '#667085', fontWeight: 500 }}>
                              {companyMfaEnforced ? 'Enabled' : 'Disabled'}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '11px', color: '#98A2B3', mt: 0.5 }}>
                            When enabled, all organization members will be required to configure MFA.
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5, color: '#717680' }}
                          >
                            Organization ID
                          </Typography>
                          <Box
                            onClick={async () => {
                              if (org?.id) {
                                try {
                                  await navigator.clipboard.writeText(org.id);
                                } catch (e) {
                                  const ta = document.createElement('textarea');
                                  ta.value = org.id;
                                  document.body.appendChild(ta);
                                  ta.select();
                                  document.execCommand('copy');
                                  document.body.removeChild(ta);
                                }
                              }
                            }}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 1.5,
                              py: 1,
                              backgroundColor: '#F9FAFB',
                              border: '1px solid #E5E7EB',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': {
                                backgroundColor: '#F3F4F6',
                              },
                            }}
                            title="Click to copy Organization ID"
                          >
                            <Typography
                              sx={{
                                fontSize: '12px',
                                fontFamily: 'monospace',
                                color: '#414651',
                                fontWeight: 500,
                              }}
                            >
                              {org?.id || 'N/A'}
                            </Typography>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#9CA3AF"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {activeSection === 'Details' && activeTab === 'Team' && (
                      <Box
                        sx={{
                          width: { xs: '100%', sm: '480px' },
                          mx: 'auto',
                          display: 'flex',
                          flexDirection: 'column',
                          overflowY: 'auto',
                          pr: 1,
                          px: { xs: 2, sm: 0 },
                          mt: 3,
                          flex: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 3,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '16px',
                              lineHeight: '24px',
                              color: '#101828',
                            }}
                          >
                            Team members
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              (async () => {
                                setTeamLoading(true);
                                try {
                                  const users = await loadUsersWithRoles();
                                setTeam(users);
                                } finally {
                                  setTeamLoading(false);
                                }
                              })();
                            }}
                            disabled={teamLoading}
                            startIcon={<RefreshIcon sx={{ fontSize: 18 }} />}
                            sx={{
                              borderColor: '#D0D5DD',
                              color: '#344054',
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: '14px',
                              borderRadius: '8px',
                              height: '36px',
                              px: 2,
                              '&:hover': {
                                borderColor: '#98A2B3',
                                bgcolor: '#F9FAFB',
                              },
                            }}
                          >
                            Reload
                          </Button>
                        </Box>
                        {teamLoading && <LinearProgress sx={{ mb: 2 }} />}
                        {teamError && (
                          <Typography sx={{ color: '#F04438', fontSize: '14px', mb: 2 }}>
                            {teamError}
                          </Typography>
                        )}

                        {canManageUsers && (
                          <Box
                            sx={{
                              mb: 3,
                              borderRadius: '12px',
                              border: '1px solid #E4E7EC',
                              bgcolor: '#F9FAFB',
                            }}
                          >
                            {/* Dropdown button header */}
                            <Box
                              onClick={() => setAddMemberOpen(!addMemberOpen)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 2,
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: 'rgba(0, 0, 0, 0.02)',
                                },
                              }}
                            >
                              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>
                                Add
                              </Typography>
                              {addMemberOpen ? (
                                <KeyboardArrowDownIcon sx={{ color: '#667085' }} />
                              ) : (
                                <KeyboardArrowRightIcon sx={{ color: '#667085' }} />
                              )}
                            </Box>
                            
                            {/* Collapsible form */}
                            <Collapse in={addMemberOpen}>
                              <Box sx={{ px: 3, pb: 3 }}>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                gap: 2,
                                mb: 2,
                              }}
                            >
                              <TextField
                                size="small"
                                label="Email"
                                placeholder="user@email.com"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    bgcolor: 'white',
                                    '& fieldset': { borderColor: '#D0D5DD' },
                                    '&:hover fieldset': { borderColor: '#98A2B3' },
                                    '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                                  },
                                  '& .MuiInputLabel-root': { color: '#344054', fontWeight: 500 },
                                }}
                              />
                              <TextField
                                size="small"
                                label="Password"
                                type="password"
                                placeholder="StrongPassword123!"
                                value={invitePassword}
                                onChange={(e) => setInvitePassword(e.target.value)}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    bgcolor: 'white',
                                    '& fieldset': { borderColor: '#D0D5DD' },
                                    '&:hover fieldset': { borderColor: '#98A2B3' },
                                    '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                                  },
                                  '& .MuiInputLabel-root': { color: '#344054', fontWeight: 500 },
                                }}
                              />
                              <TextField
                                size="small"
                                label="First name"
                                placeholder="John"
                                value={inviteFirstName}
                                onChange={(e) => setInviteFirstName(e.target.value)}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    bgcolor: 'white',
                                    '& fieldset': { borderColor: '#D0D5DD' },
                                    '&:hover fieldset': { borderColor: '#98A2B3' },
                                    '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                                  },
                                  '& .MuiInputLabel-root': { color: '#344054', fontWeight: 500 },
                                }}
                              />
                              <TextField
                                size="small"
                                label="Last name"
                                placeholder="Doe"
                                value={inviteLastName}
                                onChange={(e) => setInviteLastName(e.target.value)}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    bgcolor: 'white',
                                    '& fieldset': { borderColor: '#D0D5DD' },
                                    '&:hover fieldset': { borderColor: '#98A2B3' },
                                    '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                                  },
                                  '& .MuiInputLabel-root': { color: '#344054', fontWeight: 500 },
                                }}
                              />
                            </Box>
                            <FormControl
                              size="small"
                              fullWidth
                              sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  bgcolor: 'white',
                                  '& fieldset': { borderColor: '#D0D5DD' },
                                  '&:hover fieldset': { borderColor: '#98A2B3' },
                                  '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                                },
                              }}
                            >
                              <Select
                                value={inviteRole}
                                onChange={(e) => setInviteRole(Number(e.target.value))}
                                displayEmpty
                                renderValue={(val) => {
                                  const v = Number(val);
                                  switch (v) {
                                    case 1:
                                      return 'BILLING (0x01)';
                                    case 2:
                                      return 'WORKSPACES (0x02)';
                                    case 254:
                                      return 'ADMINISTRATOR (0xfe)';
                                    case 255:
                                      return 'OWNER (0xff)';
                                    default:
                                      return 'USER (0x00)';
                                  }
                                }}
                              >
                                <MenuItem value={0}>USER (0x00)</MenuItem>
                                <MenuItem value={1}>BILLING (0x01)</MenuItem>
                                <MenuItem value={2}>WORKSPACES (0x02)</MenuItem>
                                <MenuItem value={254}>ADMINISTRATOR (0xfe)</MenuItem>
                                <MenuItem value={255}>OWNER (0xff)</MenuItem>
                              </Select>
                            </FormControl>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Button
                                variant="contained"
                                onClick={handleInvite}
                                disabled={
                                  !inviteEmail ||
                                  !inviteFirstName ||
                                  !inviteLastName ||
                                  !invitePassword
                                }
                                sx={{
                                  bgcolor:
                                    !inviteEmail ||
                                    !inviteFirstName ||
                                    !inviteLastName ||
                                    !invitePassword
                                      ? '#F2F4F7'
                                      : '#617BFF',
                                  color:
                                    !inviteEmail ||
                                    !inviteFirstName ||
                                    !inviteLastName ||
                                    !invitePassword
                                      ? '#98A2B3'
                                      : 'white',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: '14px',
                                  borderRadius: '8px',
                                  height: '40px',
                                  px: 3,
                                  boxShadow: 'none',
                                  '&:hover': {
                                    bgcolor:
                                      !inviteEmail ||
                                      !inviteFirstName ||
                                      !inviteLastName ||
                                      !invitePassword
                                        ? '#F2F4F7'
                                        : '#4A67E3',
                                    boxShadow: 'none',
                                  },
                                  '&.Mui-disabled': {
                                    bgcolor: '#F2F4F7',
                                    color: '#98A2B3',
                                  },
                                }}
                              >
                                Invite
                              </Button>
                            </Box>
                              </Box>
                            </Collapse>
                          </Box>
                        )}

                        {!teamLoading && team.length === 0 && !teamError && (
                          <Typography
                            sx={{ color: '#667085', fontSize: '14px', textAlign: 'center', py: 4 }}
                          >
                            No team members yet.
                          </Typography>
                        )}
                        {team.map((member, idx) => {
                          const isEditing = editingMemberId === member.id;
                          const memberRole = member.orgRole ?? 0;
                          
                          return (
                          <Box
                            key={member.id || idx}
                            sx={{
                              width: '100%',
                              minHeight: '72px',
                              borderRadius: '12px',
                              border: '1px solid #E4E7EC',
                              padding: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 2,
                              bgcolor: 'white',
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: '#D0D5DD',
                                boxShadow: '0 1px 3px rgba(16, 24, 40, 0.1)',
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                              <Avatar
                                src={(member as any).imageUrl || ''}
                                alt={member.name || member.email}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: '50%',
                                  border: '2px solid #F2F4F7',
                                }}
                              />
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    color: '#101828',
                                  }}
                                >
                                  {member.name || member.email}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    color: '#667085',
                                  }}
                                >
                                  {member.email}
                                </Typography>
                                {/* Role display or selector */}
                                {isEditing && canManageUsers ? (
                                  <Box sx={{ mt: 1 }}>
                                    <FormControl size="small" sx={{ minWidth: 200 }}>
                                      <Select
                                        value={editingRole}
                                        onChange={(e) => setEditingRole(Number(e.target.value))}
                                        sx={{
                                          fontSize: '12px',
                                          '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#D0D5DD',
                                          },
                                        }}
                                      >
                                        <MenuItem value={0}>USER (0x00)</MenuItem>
                                        <MenuItem value={1}>BILLING (0x01)</MenuItem>
                                        <MenuItem value={2}>WORKSPACES (0x02)</MenuItem>
                                        <MenuItem value={254}>ADMINISTRATOR (0xfe)</MenuItem>
                                        <MenuItem value={255}>OWNER (0xff)</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Box>
                                ) : (
                                  <Typography
                                    sx={{
                                      fontWeight: 400,
                                      fontSize: '12px',
                                      lineHeight: '18px',
                                      color: '#98A2B3',
                                      mt: 0.5,
                                    }}
                                  >
                                    {getRoleName(memberRole)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {isEditing ? (
                                <>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleCancelEdit}
                                    sx={{
                                      textTransform: 'none',
                                      fontSize: '12px',
                                      borderColor: '#D0D5DD',
                                      color: '#344054',
                                      minWidth: 'auto',
                                      px: 1.5,
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => handleSaveRole(member.id)}
                                    sx={{
                                      textTransform: 'none',
                                      fontSize: '12px',
                                      bgcolor: '#617BFF',
                                      minWidth: 'auto',
                                      px: 1.5,
                                      '&:hover': {
                                        bgcolor: '#5A6FE8',
                                      },
                                    }}
                                  >
                                    Save
                                  </Button>
                                </>
                              ) : (
                                <>
                                  {canManageUsers && (
                                    <IconButton
                                      onClick={() => handleEditRole(member.id, memberRole)}
                                      title="Edit"
                                      sx={{
                                        color: '#667085',
                                        '&:hover': {
                                          color: '#617BFF',
                                          bgcolor: '#F5F7FF',
                                        },
                                      }}
                                    >
                                      <EditIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                  )}
                                  <IconButton
                                    onClick={() => {
                                      if ((authState.user?.email || '') === member.email) return;
                                      if (window.confirm('Remove this user from the organization?')) {
                                        handleDelete(member.id);
                                      }
                                    }}
                                    disabled={(authState.user?.email || '') === member.email}
                                    title={
                                      (authState.user?.email || '') === member.email
                                        ? 'You cannot delete your own account'
                                        : 'Delete user'
                                    }
                                    sx={{
                                      color: '#667085',
                                      '&:hover': {
                                        color: '#F04438',
                                        bgcolor: '#FEF3F2',
                                      },
                                      '&:disabled': {
                                        color: '#D0D5DD',
                                      },
                                    }}
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                                  </IconButton>
                                </>
                              )}
                            </Box>
                          </Box>
                          );
                        })}
                        <Box sx={{ height: 24 }} />
                      </Box>
                    )}

                    {activeSection === 'Details' && activeTab === 'Workspace' && (
                      <Box sx={{ p: 0 }}>
                        <WorkspacesTab />
                      </Box>
                    )}

                    {activeSection === 'Plan' && (
                      <Box
                        sx={{
                          p: { xs: 2, sm: 4 },
                          flex: 1,
                          overflowY: 'auto',
                          position: 'relative',
                        }}
                      >
                        {/* Credits mode: Credit balance + Buy packs */}
                        {creditsLoading && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        )}
                        {creditsMode && creditsBalance != null && !creditsLoading && (
                          <Box sx={{ mb: 4 }}>
                            <Card sx={{ borderRadius: '12px', boxShadow: 'none', border: '1px solid #E9EAEB', p: 2, mb: 2 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#101828', mb: 1 }}>Credit balance</Typography>
                              <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#101828' }}>{creditsBalance.realtimeBalance.toLocaleString()} credits</Typography>
                              <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.5 }}>
                                Available now
                                {creditsBalance.pendingConsumption > 0 && <> · {creditsBalance.pendingConsumption.toLocaleString()} pending</>}
                              </Typography>
                            </Card>
                            <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#101828', mb: 2 }}>Buy credit packs</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                              {creditPacks.map((pack) => (
                                <Box key={pack.id} sx={{ border: '1px solid #E5E7EB', borderRadius: '8px', p: 2, minWidth: 140, textAlign: 'center' }}>
                                  <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>{pack.name}</Typography>
                                  <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#101828' }}>{pack.credits} credits</Typography>
                                  <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>${pack.price}</Typography>
                                  <Button size="small" variant="contained" onClick={() => handleBuyCreditsPack(pack.id)} sx={{ mt: 1, textTransform: 'none', borderRadius: '8px', backgroundColor: '#617BFF', '&:hover': { backgroundColor: '#5A6FE8' } }}>
                                    Buy
                                  </Button>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                        {/* Current Subscription Status - Dedicated Section */}
                        <Box sx={{ mb: 4 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: '16px', sm: '18px' },
                              color: '#101828',
                              mb: 2,
                            }}
                          >
                            Current Subscription
                          </Typography>
                          <Card
                            ref={subCardRef}
                            sx={{
                              borderRadius: '12px',
                              boxShadow: 'none',
                              bgcolor: 'white',
                              border: '2px solid #617BFF',
                              width: { xs: '100%', sm: '468px' },
                              minHeight: '120px',
                              position: 'relative',
                            }}
                          >
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  alignItems: { xs: 'flex-start', sm: 'center' },
                                  justifyContent: 'space-between',
                                  gap: { xs: 1, sm: 0 },
                                  mb: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: 1.5,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: { xs: '14px', sm: '16px' },
                                      color: '#101828',
                                    }}
                                  >
                                    {userQuota?.subscription?.name ||
                                      currentSubscription?.name ||
                                      'Free Trial'}
                                  </Typography>
                                  <Chip
                                    label="Active"
                                    size="small"
                                    sx={{
                                      height: 22,
                                      bgcolor: '#10B981',
                                      color: 'white',
                                      fontWeight: 600,
                                      fontSize: '12px',
                                    }}
                                  />
                                  <Chip
                                    label={currentSubscription?.periodType || 'YEARLY'}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      borderRadius: '12px',
                                      bgcolor: '#F2F4F7',
                                      color: '#414651',
                                      fontWeight: 600,
                                      fontSize: '12px',
                                    }}
                                  />
                                </Box>
                                <Typography
                                  sx={{
                                    fontSize: { xs: '20px', sm: '24px' },
                                    fontWeight: 700,
                                    color: '#101828',
                                  }}
                                >
                                  {(() => {
                                    // For free plans, show $0
                                    const planName = (
                                      userQuota?.subscription?.name ||
                                      currentSubscription?.name ||
                                      ''
                                    ).toLowerCase();
                                    const isFree =
                                      planName.includes('free') || planName.includes('trial');
                                    const price = isFree ? 0 : currentSubscription?.price || 300;
                                    return `$${price}`;
                                  })()}
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: { xs: '12px', sm: '14px' },
                                      color: '#6B7280',
                                      ml: 0.5,
                                    }}
                                  >
                                    per{' '}
                                    {currentSubscription?.periodType === 'YEARLY'
                                      ? 'year'
                                      : 'month'}
                                  </Typography>
                                </Typography>
                              </Box>

                              {/* Subscription dates */}
                              {currentSubscription?.dateFrom && currentSubscription?.dateTo && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography sx={{ fontSize: '12px', color: '#717680', mb: 0.5 }}>
                                    Subscription Period
                                  </Typography>
                                  <Typography sx={{ fontSize: '13px', color: '#535862' }}>
                                    {new Date(currentSubscription.dateFrom).toLocaleDateString(
                                      'en-US',
                                      {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                      }
                                    )}{' '}
                                    -{' '}
                                    {new Date(currentSubscription.dateTo).toLocaleDateString(
                                      'en-US',
                                      {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                      }
                                    )}
                                  </Typography>
                                </Box>
                              )}

                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-end',
                                }}
                              >
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => setUpgradePlanModalOpen(true)}
                                    sx={{
                                      textTransform: 'none',
                                      borderRadius: '8px',
                                      backgroundColor: '#617BFF',
                                      color: 'white',
                                      fontSize: '12px',
                                      fontWeight: 600,
                                      '&:hover': {
                                        backgroundColor: '#5A6FE8',
                                      },
                                    }}
                                  >
                                    Upgrade
                                  </Button>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Box>

                        {/* Hover details panel for Subscription card (like sidebar) */}
                        {showSubDetails &&
                          (() => {
                            // Find current plan details from subscriptions list
                            const currentPlan = subscriptions.find(
                              (s) => s.name === currentSubscription?.name
                            );
                            const currentPeriod = currentPlan?.periods?.find(
                              (p) => p.periodType === currentSubscription?.periodType
                            );

                            return (
                              <Box
                                sx={{
                                  position: 'fixed',
                                  zIndex: 9999,
                                  left: { xs: 0, sm: subPanelPos.left },
                                  top: { xs: 0, sm: subPanelPos.top },
                                  width: { xs: '100%', sm: '340px' },
                                  right: { xs: 0, sm: 'auto' },
                                  bgcolor: 'white',
                                  border: '1px solid #E9EAEB',
                                  borderRadius: '12px',
                                  p: 2.5,
                                  boxShadow: '0 8px 24px rgba(16,24,40,0.12)',
                                  maxHeight: '600px',
                                  overflowY: 'auto',
                                }}
                                onMouseEnter={() => setShowSubDetails(true)}
                                onMouseLeave={() => setShowSubDetails(false)}
                              >
                                <Typography
                                  sx={{
                                    fontSize: '16px',
                                    fontWeight: 700,
                                    color: '#101828',
                                    mb: 2,
                                  }}
                                >
                                  Plan Usage
                                </Typography>

                                {/* Plan Type */}
                                <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #E9EAEB' }}>
                                  <Typography
                                    sx={{
                                      fontSize: '13px',
                                      fontWeight: 600,
                                      color: '#717680',
                                      mb: 0.5,
                                    }}
                                  >
                                    Current Plan
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: '14px', fontWeight: 600, color: '#101828' }}
                                  >
                                    {quotaData?.subscriptionName || currentPlan?.name || 'Business'}
                                  </Typography>
                                </Box>

                                {/* Bridge Nodes Traffic - Removed as not in API */}

                                {/* Resources Section */}
                                <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #E9EAEB' }}>
                                  <Typography
                                    sx={{
                                      fontSize: '13px',
                                      fontWeight: 600,
                                      color: '#717680',
                                      mb: 1,
                                    }}
                                  >
                                    Resources
                                  </Typography>

                                  {/* Buckets */}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      mb: 0.75,
                                    }}
                                  >
                                    <Typography sx={{ fontSize: '12px', color: '#535862' }}>
                                      Buckets
                                    </Typography>
                                    <Typography
                                      sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}
                                    >
                                      {(() => {
                                        const bucketsQuota =
                                          getQuotaByCode('buckets.max') ||
                                          getQuotaByCode('storage.units');
                                        if (bucketsQuota) {
                                          return `${bucketsCount} / ${bucketsQuota.usage.max}`;
                                        }
                                        return `${bucketsCount} / Unlimited`;
                                      })()}
                                    </Typography>
                                  </Box>

                                  {/* Repositories */}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      mb: 0.75,
                                    }}
                                  >
                                    <Typography sx={{ fontSize: '12px', color: '#535862' }}>
                                      Repositories
                                    </Typography>
                                    <Typography
                                      sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}
                                    >
                                      {(() => {
                                        const reposQuota =
                                          getQuotaByCode('repos.max') ||
                                          getQuotaByCode('repositories.max');
                                        if (reposQuota) {
                                          return `${repositoriesCount} / ${reposQuota.usage.max}`;
                                        }
                                        return `${repositoriesCount} / Unlimited`;
                                      })()}
                                    </Typography>
                                  </Box>

                                  {/* Bridge Nodes */}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      mb: 0.75,
                                    }}
                                  >
                                    <Typography sx={{ fontSize: '12px', color: '#535862' }}>
                                      Private Bridge Nodes
                                    </Typography>
                                    <Typography
                                      sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}
                                    >
                                      {(() => {
                                        const nodesQuota =
                                          getQuotaByCode('nodes.max') ||
                                          getQuotaByCode('bridge.nodes') ||
                                          getQuotaByCode('nodes.private');
                                        if (nodesQuota) {
                                          return `${nodesQuota.usage.current} / ${nodesQuota.usage.max}`;
                                        }
                                        return `0 / Unlimited`;
                                      })()}
                                    </Typography>
                                  </Box>

                                  {/* Users/Team Members */}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      mb: 0.75,
                                    }}
                                  >
                                    <Typography sx={{ fontSize: '12px', color: '#535862' }}>
                                      Team Members
                                    </Typography>
                                    <Typography
                                      sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}
                                    >
                                      {(() => {
                                        const teamQuota =
                                          getQuotaByCode('users.max') ||
                                          getQuotaByCode('team.max') ||
                                          getQuotaByCode('members.max');
                                        if (teamQuota) {
                                          return `${team.length} / ${teamQuota.usage.max}`;
                                        }
                                        return `${team.length} / Unlimited`;
                                      })()}
                                    </Typography>
                                  </Box>

                                  {/* Provider Integrations */}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: '12px', color: '#535862' }}>
                                      Provider Integrations
                                    </Typography>
                                    <Typography
                                      sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}
                                    >
                                      {(() => {
                                        const providersQuota =
                                          getQuotaByCode('providers.max') ||
                                          getQuotaByCode('integrations.max');
                                        const providers = 3; // Placeholder - could calculate from buckets
                                        if (providersQuota) {
                                          return `${providers} / ${providersQuota.usage.max}`;
                                        }
                                        return `${providers} / Unlimited`;
                                      })()}
                                    </Typography>
                                  </Box>
                                </Box>

                                {/* Features Access */}
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: '13px',
                                      fontWeight: 600,
                                      color: '#717680',
                                      mb: 1,
                                    }}
                                  >
                                    Available Features
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(() => {
                                      // Get features from current plan/period or use defaults
                                      const features = (currentPeriod as any)?.features ||
                                        (currentPlan as any)?.features || [
                                          'Public API',
                                          'Statistics',
                                          'Metrics',
                                          'Overview',
                                          'Advanced Filtering',
                                        ];
                                      return features.map((feature: string) => (
                                        <Chip
                                          key={feature}
                                          label={feature}
                                          size="small"
                                          sx={{
                                            height: '22px',
                                            fontSize: '11px',
                                            bgcolor: '#F0FDF4',
                                            color: '#0E9F5D',
                                            border: '1px solid #D1FAE5',
                                            fontWeight: 500,
                                          }}
                                        />
                                      ));
                                    })()}
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })()}
                        
                        {/* Check if any limit is close to being exceeded */}
                        {(() => {
                          const checkLimitExceeded = () => {
                            // Check buckets
                            const bucketsQuota = getQuotaByCode('buckets.max') || getQuotaByCode('storage.units');
                            if (bucketsQuota && bucketsQuota.usage.max > 0) {
                              const percentage = (bucketsCount / bucketsQuota.usage.max) * 100;
                              if (percentage >= 80) return { resource: 'Buckets', percentage, current: bucketsCount, max: bucketsQuota.usage.max };
                            }
                            
                            // Check repositories
                            const reposQuota = getQuotaByCode('repos.max') || getQuotaByCode('repositories.max');
                            if (reposQuota && reposQuota.usage.max > 0) {
                              const percentage = (repositoriesCount / reposQuota.usage.max) * 100;
                              if (percentage >= 80) return { resource: 'Repositories', percentage, current: repositoriesCount, max: reposQuota.usage.max };
                            }
                            
                            // Check nodes
                            const nodesQuota = getQuotaByCode('nodes.max') || getQuotaByCode('bridge.nodes') || getQuotaByCode('nodes.private');
                            if (nodesQuota && nodesQuota.usage.max > 0) {
                              const percentage = (nodesQuota.usage.current / nodesQuota.usage.max) * 100;
                              if (percentage >= 80) return { resource: 'Private Bridge Nodes', percentage, current: nodesQuota.usage.current, max: nodesQuota.usage.max };
                            }
                            
                            // Check team members
                            const usersQuota = getQuotaByCode('users.max') || getQuotaByCode('team.members');
                            if (usersQuota && usersQuota.usage.max > 0) {
                              const percentage = (usersQuota.usage.current / usersQuota.usage.max) * 100;
                              if (percentage >= 80) return { resource: 'Team Members', percentage, current: usersQuota.usage.current, max: usersQuota.usage.max };
                            }
                            
                            return null;
                          };
                          
                          const limitWarning = checkLimitExceeded();
                          
                          return limitWarning && (
                            <Alert 
                              severity="warning" 
                              sx={{ 
                                mb: 2, 
                                borderRadius: '12px',
                                border: '1px solid #FDB022',
                                bgcolor: '#FFFBEB',
                                width: { xs: '100%', sm: '468px' },
                                alignItems: 'center',
                                '& .MuiAlert-icon': {
                                  color: '#F79009'
                                },
                                '& .MuiAlert-message': {
                                  width: '100%',
                                  py: 0.5
                                },
                                '& .MuiAlert-action': {
                                  pt: 0,
                                  pl: 2,
                                  mr: 0
                                }
                              }}
                              action={
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => setActiveSection('Plan')}
                                  sx={{
                                    bgcolor: '#617BFF',
                                    color: 'white',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    minWidth: 'auto',
                                    px: 2,
                                    py: 0.5,
                                    height: '28px',
                                    borderRadius: '6px',
                                    boxShadow: 'none',
                                    flexShrink: 0,
                                    '&:hover': {
                                      bgcolor: '#4A67E3',
                                      boxShadow: 'none',
                                    },
                                  }}
                                >
                                  Upgrade
                                </Button>
                              }
                            >
                              <Box>
                                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#92400E', mb: 0.5, lineHeight: 1.4 }}>
                                  {limitWarning.percentage >= 100 ? 'Limit Reached' : 'Approaching Limit'}
                                </Typography>
                                <Typography sx={{ fontSize: '13px', color: '#92400E', lineHeight: 1.5 }}>
                                  You're using {limitWarning.current} of {limitWarning.max} {limitWarning.resource.toLowerCase()} ({Math.round(limitWarning.percentage)}%). {limitWarning.percentage >= 100 ? 'Upgrade to add more.' : 'Consider upgrading your plan.'}
                                </Typography>
                              </Box>
                            </Alert>
                          );
                        })()}

                        {/* Plan Usage Card */}
                        <Card
                          sx={{
                            mb: 3,
                            borderRadius: '12px',
                            boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
                            bgcolor: 'white',
                            border: '1px solid #E9EAEB',
                            width: { xs: '100%', sm: '468px' },
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Typography
                              sx={{
                                fontSize: '16px',
                                fontWeight: 700,
                                color: '#101828',
                                mb: 2,
                              }}
                            >
                              Plan Usage
                            </Typography>

                            {/* Plan Type */}
                            <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #E9EAEB' }}>
                              <Typography
                                sx={{
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  color: '#717680',
                                  mb: 0.5,
                                }}
                              >
                                Current Plan
                              </Typography>
                              <Typography
                                sx={{ fontSize: '14px', fontWeight: 600, color: '#101828' }}
                              >
                                {quotaData?.subscriptionName || 'Free'}
                              </Typography>
                            </Box>

                            {/* Resources Section */}
                            <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #E9EAEB' }}>
                              <Typography
                                sx={{
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  color: '#717680',
                                  mb: 1,
                                }}
                              >
                                Resources
                              </Typography>

                              {/* Buckets */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                <Typography sx={{ fontSize: '12px', color: '#535862' }}>
                                  Buckets
                                </Typography>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}>
                                  {(() => {
                                    const bucketsQuota = getQuotaByCode('buckets.max') || getQuotaByCode('storage.units');
                                    if (bucketsQuota) {
                                      return `${bucketsCount} / ${bucketsQuota.usage.max}`;
                                    }
                                    return `${bucketsCount} / Unlimited`;
                                  })()}
                                </Typography>
                              </Box>

                              {/* Repositories */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                <Typography sx={{ fontSize: '12px', color: '#535862' }}>
                                  Repositories
                                </Typography>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}>
                                  {(() => {
                                    const reposQuota = getQuotaByCode('repos.max') || getQuotaByCode('repositories.max');
                                    if (reposQuota) {
                                      return `${repositoriesCount} / ${reposQuota.usage.max}`;
                                    }
                                    return `${repositoriesCount} / Unlimited`;
                                  })()}
                                </Typography>
                              </Box>

                              {/* Private Bridge Nodes */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                <Typography sx={{ fontSize: '12px', color: '#535862' }}>
                                  Private Bridge Nodes
                                </Typography>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}>
                                  {(() => {
                                    const nodesQuota = getQuotaByCode('nodes.max') || getQuotaByCode('bridge.nodes') || getQuotaByCode('nodes.private');
                                    if (nodesQuota) {
                                      return `${nodesQuota.usage.current} / ${nodesQuota.usage.max}`;
                                    }
                                    return `0 / Unlimited`;
                                  })()}
                                </Typography>
                              </Box>

                              {/* Users/Team Members */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                <Typography sx={{ fontSize: '12px', color: '#535862' }}>
                                  Team Members
                                </Typography>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}>
                                  {(() => {
                                    const usersQuota = getQuotaByCode('users.max') || getQuotaByCode('team.members');
                                    if (usersQuota) {
                                      return `${usersQuota.usage.current} / ${usersQuota.usage.max}`;
                                    }
                                    return `0 / Unlimited`;
                                  })()}
                                </Typography>
                              </Box>

                              {/* Provider Integrations */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography sx={{ fontSize: '12px', color: '#535862' }}>
                                  Provider Integrations
                                </Typography>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}>
                                  {(() => {
                                    const providersQuota = getQuotaByCode('providers.max') || getQuotaByCode('integrations.max');
                                    const providers = 3; // Placeholder - could calculate from buckets
                                    if (providersQuota) {
                                      return `${providers} / ${providersQuota.usage.max}`;
                                    }
                                    return `${providers} / Unlimited`;
                                  })()}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Features Access */}
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  color: '#717680',
                                  mb: 1,
                                }}
                              >
                                Available Features
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {['Public API', 'Statistics', 'Metrics', 'Overview', 'Advanced Filtering'].map((feature: string) => (
                                  <Chip
                                    key={feature}
                                    label={feature}
                                    size="small"
                                    sx={{
                                      height: '22px',
                                      fontSize: '11px',
                                      bgcolor: '#F0FDF4',
                                      color: '#0E9F5D',
                                      border: '1px solid #D1FAE5',
                                      fontWeight: 500,
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>

                            {/* CTA Button */}
                            <Box sx={{ mt: 2.5, pt: 2.5, borderTop: '1px solid #E9EAEB', display: 'flex', justifyContent: 'flex-end' }}>
                              <Button
                                variant="contained"
                                onClick={() => setUpgradePlanModalOpen(true)}
                                endIcon={
                                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 8h10M8 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                }
                                sx={{
                                  bgcolor: '#617BFF',
                                  color: 'white',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: '13px',
                                  px: 2.5,
                                  py: 1,
                                  borderRadius: '8px',
                                  boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
                                  '&:hover': {
                                    bgcolor: '#4A67E3',
                                    boxShadow: '0px 1px 3px rgba(16, 24, 40, 0.1)',
                                  },
                                }}
                              >
                                Upgrade Plan
                              </Button>
                            </Box>

                          </CardContent>
                        </Card>

                        {/* Available Plans Section - Only show for Free users */}
                        {userQuota?.subscription?.name && (userQuota.subscription.name.toLowerCase().includes('free') || userQuota.subscription.name.toLowerCase().includes('trial')) && (
                          <>
                            <Box sx={{ mb: 3 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: '16px', sm: '18px' },
                              color: '#101828',
                              mb: 2,
                            }}
                          >
                            Available Plans
                          </Typography>

                          {/* Period Type Toggle */}
                          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                bgcolor: '#F3F4F6',
                                borderRadius: '8px',
                                padding: '4px',
                                gap: '4px',
                              }}
                            >
                              <Button
                                onClick={() => setSelectedPeriodType(QuotaTypes.PeriodType.MONTHLY)}
                                sx={{
                                  px: { xs: 2, sm: 3 },
                                  py: 1,
                                  borderRadius: '6px',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: '14px',
                                  bgcolor:
                                    selectedPeriodType === QuotaTypes.PeriodType.MONTHLY
                                      ? 'white'
                                      : 'transparent',
                                  color:
                                    selectedPeriodType === QuotaTypes.PeriodType.MONTHLY
                                      ? '#101828'
                                      : '#6B7280',
                                  boxShadow:
                                    selectedPeriodType === QuotaTypes.PeriodType.MONTHLY
                                      ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                                      : 'none',
                                  '&:hover': {
                                    bgcolor:
                                      selectedPeriodType === QuotaTypes.PeriodType.MONTHLY
                                        ? 'white'
                                        : 'rgba(0, 0, 0, 0.04)',
                                  },
                                }}
                              >
                                Monthly
                              </Button>
                              <Button
                                onClick={() => setSelectedPeriodType(QuotaTypes.PeriodType.YEARLY)}
                                sx={{
                                  px: { xs: 2, sm: 3 },
                                  py: 1,
                                  borderRadius: '6px',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: '14px',
                                  bgcolor:
                                    selectedPeriodType === QuotaTypes.PeriodType.YEARLY
                                      ? 'white'
                                      : 'transparent',
                                  color:
                                    selectedPeriodType === QuotaTypes.PeriodType.YEARLY
                                      ? '#101828'
                                      : '#6B7280',
                                  boxShadow:
                                    selectedPeriodType === QuotaTypes.PeriodType.YEARLY
                                      ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                                      : 'none',
                                  '&:hover': {
                                    bgcolor:
                                      selectedPeriodType === QuotaTypes.PeriodType.YEARLY
                                        ? 'white'
                                        : 'rgba(0, 0, 0, 0.04)',
                                  },
                                }}
                              >
                                Yearly
                                <Chip
                                  label="Save 20%"
                                  size="small"
                                  sx={{
                                    ml: 1,
                                    height: '20px',
                                    fontSize: '11px',
                                    bgcolor: '#10B981',
                                    color: 'white',
                                    fontWeight: 600,
                                  }}
                                />
                              </Button>
                            </Box>
                          </Box>

                        {/* Available Plans from API */}
                        {subscriptionLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={32} />
                          </Box>
                        ) : subscriptions.length > 0 ? (
                          subscriptions.map((plan) => {
                            const selectedPeriod = plan.periods.find(
                              (p) => p.periodType === selectedPeriodType
                            );
                            const isCurrentPlanType = currentSubscription?.name === plan.name;
                            const isCurrentPlanPeriod =
                              currentSubscription?.periodType === selectedPeriodType;
                            const isExactCurrentPlan = isCurrentPlanType && isCurrentPlanPeriod;

                            return (
                              <Card
                                key={plan.id}
                                sx={{
                                  mb: 2.5,
                                  borderRadius: '12px',
                                  boxShadow: 'none',
                                  bgcolor: 'white',
                                  border: isExactCurrentPlan
                                    ? '2px solid #E9EAEB'
                                    : '1px solid #E9EAEB',
                                  width: { xs: '100%', sm: '468px' },
                                  minHeight: '237px',
                                  opacity: isExactCurrentPlan ? 0.6 : 1,
                                }}
                              >
                                <CardContent
                                  sx={{
                                    p: { xs: 2, sm: 3 },
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: { xs: 'column', sm: 'row' },
                                      justifyContent: 'space-between',
                                      alignItems: { xs: 'flex-start', sm: 'center' },
                                      gap: { xs: 1, sm: 0 },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: { xs: '14px', sm: '16px' },
                                          lineHeight: '24px',
                                          color: '#101828',
                                        }}
                                      >
                                        {plan.name}
                                        {isExactCurrentPlan && (
                                          <Chip
                                            label="Current Plan"
                                            size="small"
                                            sx={{
                                              ml: 1,
                                              height: 20,
                                              bgcolor: '#F2F4F7',
                                              color: '#414651',
                                              fontWeight: 600,
                                              fontSize: '11px',
                                            }}
                                          />
                                        )}
                                      </Typography>
                                      {selectedPeriod && (
                                        <Chip
                                          label={formatPeriodType(
                                            selectedPeriod.periodType as QuotaTypes.PeriodType
                                          )}
                                          size="small"
                                          sx={{
                                            height: 24,
                                            borderRadius: '12px',
                                            bgcolor: '#F2F4F7',
                                            color: '#414651',
                                            fontWeight: 600,
                                          }}
                                        />
                                      )}
                                    </Box>
                                    {selectedPeriod && (
                                      <Box
                                        sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: { xs: '24px', sm: '32px' },
                                            fontWeight: 700,
                                            color: '#101828',
                                          }}
                                        >
                                          {formatPrice(selectedPeriod.price)}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: { xs: '12px', sm: '14px' },
                                            color: '#6B7280',
                                          }}
                                        >
                                          per{' '}
                                          {selectedPeriod.periodType ===
                                          QuotaTypes.PeriodType.MONTHLY
                                            ? 'month'
                                            : selectedPeriod.periodType ===
                                              QuotaTypes.PeriodType.YEARLY
                                            ? 'year'
                                            : selectedPeriod.periodType.toLowerCase()}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                  <Typography
                                    sx={{
                                      mt: 1,
                                      fontSize: { xs: '13px', sm: '14px' },
                                      lineHeight: '20px',
                                      color: '#535862',
                                    }}
                                  >
                                    {plan.description || 'Upgrade your organization capabilities'}
                                  </Typography>
                                  <Divider sx={{ my: 2 }} />
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {isExactCurrentPlan ? (
                                      <>
                                        <Typography
                                          sx={{
                                            color: '#6B7280',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                          }}
                                        >
                                          This is your current plan
                                        </Typography>
                                        <Button
                                          variant="outlined"
                                          onClick={() => setUpgradePlanModalOpen(true)}
                                          sx={{
                                            bgcolor: 'transparent',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderRadius: '8px',
                                            borderColor: '#E9EAEB',
                                            color: '#414651',
                                            '&:hover': {
                                              borderColor: '#617BFF',
                                              bgcolor: 'rgba(97, 123, 255, 0.04)',
                                            },
                                          }}
                                        >
                                          Manage Plan
                                        </Button>
                                      </>
                                    ) : (
                                      <>
                                        <Button
                                          variant="text"
                                          sx={{
                                            color: '#414651',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            p: 0,
                                          }}
                                        >
                                          Learn more
                                        </Button>
                                        {selectedPeriod && (
                                          <Button
                                            variant="contained"
                                            endIcon={
                                              subscriptionLoading ? (
                                                <CircularProgress size={16} color="inherit" />
                                              ) : (
                                                <ArrowForwardIcon />
                                              )
                                            }
                                            onClick={() => handleBuySubscription(selectedPeriod.id)}
                                            disabled={subscriptionLoading}
                                            sx={{
                                              bgcolor: '#617BFF',
                                              textTransform: 'none',
                                              fontWeight: 600,
                                              borderRadius: '8px',
                                              '&:hover': { bgcolor: '#4A67E3' },
                                            }}
                                          >
                                            Subscribe
                                          </Button>
                                        )}
                                      </>
                                    )}
                                  </Box>
                                </CardContent>
                              </Card>
                            );
                          })
                        ) : (
                          <Typography sx={{ color: '#6B7280', fontSize: '14px', py: 4 }}>
                            No subscription plans available
                          </Typography>
                        )}
                            </Box>
                          </>
                        )}

                        {/* COMMENTED OUT: Pay as you need section */}
                        {false && (
                        <Card
                          sx={{
                            mb: 2.5,
                            borderRadius: '12px',
                            boxShadow: 'none',
                            bgcolor: 'white',
                            border: '1px solid #E9EAEB',
                            width: { xs: '100%', sm: '468px' },
                            height: 'auto',
                            minHeight: { sm: '193px' },
                          }}
                        >
                          <CardContent
                            sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: '16px',
                                lineHeight: '24px',
                                color: '#101828',
                              }}
                            >
                              Pay as you need
                            </Typography>
                            <Typography
                              sx={{ mt: 1, fontSize: '14px', lineHeight: '20px', color: '#535862' }}
                            >
                              Flashback can scale as you grow, offering a pay-as-you-need transfer
                              capacity option. Only pay for what you use.
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Button
                                variant="text"
                                disabled
                                sx={{
                                  color: '#A4A7AE',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  p: 0,
                                  '&.Mui-disabled': { color: '#A4A7AE' },
                                }}
                              >
                                Learn more
                              </Button>
                              <Button
                                variant="contained"
                                disabled
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                  bgcolor: '#E9EAEB',
                                  color: '#A4A7AE',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  borderRadius: '8px',
                                  '&.Mui-disabled': { bgcolor: '#E9EAEB', color: '#A4A7AE' },
                                }}
                              >
                                Choose amount
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                        )}
                      </Box>
                    )}

                    {activeSection === 'Billing' && (
                      <Box sx={{ p: { xs: 2, sm: 4 }, flex: 1, overflowY: 'auto' }}>
                        {/* Credits mode: Credit transactions */}
                        {creditsMode && (
                          <Box sx={{ mb: 4 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#101828', mb: 2 }}>Credit transactions</Typography>
                            <Card sx={{ borderRadius: '12px', boxShadow: 'none', border: '1px solid #E9EAEB', minHeight: 200 }}>
                              <CardContent sx={{ p: 0 }}>
                                <Box sx={{ height: 44, bgcolor: '#FAFAFA', borderBottom: '1px solid #E9EAEB', display: 'flex', alignItems: 'center', px: 2 }}>
                                  <Box sx={{ width: 100, flexShrink: 0 }}><Typography sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}>Date</Typography></Box>
                                  <Box sx={{ width: 80, flexShrink: 0 }}><Typography sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}>Type</Typography></Box>
                                  <Box sx={{ flex: 1, minWidth: 0 }}><Typography sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}>Description</Typography></Box>
                                  <Box sx={{ width: 72, flexShrink: 0, textAlign: 'right' }}><Typography sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}>Amount</Typography></Box>
                                </Box>
                                <Box sx={{ minHeight: 156 }}>
                                  {creditTransactions.length === 0 ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                                      <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>No transactions yet</Typography>
                                    </Box>
                                  ) : (
                                    creditTransactions.slice(0, 10).map((tx) => (
                                      <Box key={tx.id} sx={{ height: 40, display: 'flex', alignItems: 'center', px: 2, borderBottom: '1px solid #E9EAEB' }}>
                                        <Box sx={{ width: 100, flexShrink: 0 }}><Typography sx={{ fontSize: '13px', color: '#414651' }}>{new Date(tx.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Typography></Box>
                                        <Box sx={{ width: 80, flexShrink: 0 }}><Typography sx={{ fontSize: '12px', color: '#535862' }}>{tx.type}</Typography></Box>
                                        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          <Typography sx={{ fontSize: '13px', color: '#414651' }} noWrap>
                                            {tx.description ?? (tx.subscriptionName ? `Subscription: ${tx.subscriptionName}` : tx.packName ? `Pack: ${tx.packName}` : '—')}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ width: 72, flexShrink: 0, textAlign: 'right' }}><Typography sx={{ fontSize: '13px', fontWeight: 500, color: tx.creditAmount >= 0 ? '#10B981' : '#101828' }}>{tx.creditAmount >= 0 ? '+' : ''}{formatCreditAmount(tx.creditAmount)}</Typography></Box>
                                      </Box>
                                    ))
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          </Box>
                        )}
                        {/* ========== PAYMENT METHOD SECTION - COMMENTED OUT ==========
                  <Card sx={{ mb: 2.5, borderRadius: '12px', boxShadow: 'none', bgcolor: 'white', border: '1px solid #E9EAEB', width: '468px', height: addingMethod ? 'auto' : '274px' }}>
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '16px', lineHeight: '24px', color: '#101828' }}>Payment method</Typography>
                        <Typography variant="body2" color="#535862" sx={{ fontSize: '14px', lineHeight: '20px', mt: 0.5 }}>
                          {addingMethod ? 'Update your billing details and address.' : 'Change how you pay for your plan.'}
                        </Typography>
                      </Box>
                      {addingMethod ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#414651', mb: 0.5 }}>Card details*</Typography>
                              <TextField placeholder="Name on card" fullWidth sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px' } }} />
                            </Box>
                            <Box sx={{ width: '160px' }}>
                              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#414651', mb: 0.5 }}>Expiry*</Typography>
                              <TextField placeholder="06 / 2025" fullWidth sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px' } }} />
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#414651', mb: 0.5 }}>Card number*</Typography>
                              <TextField placeholder="1234 1234 1234 1234" fullWidth sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px' } }} />
                            </Box>
                            <Box sx={{ width: '160px' }}>
                              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#414651', mb: 0.5 }}>CVV*</Typography>
                              <TextField placeholder="•••" fullWidth sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px' } }} />
                            </Box>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#414651', mb: 0.5 }}>Billing address*</Typography>
                            <TextField placeholder="Start typing" fullWidth InputProps={{ startAdornment: (<InputAdornment position="start"><LocationOnIcon sx={{ color: '#A4A7AE' }} /></InputAdornment>) }} sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px' } }} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#414651', mb: 0.5 }}>Email address*</Typography>
                            <TextField placeholder="billing@flashback.tech" fullWidth InputProps={{ startAdornment: (<InputAdornment position="start"><EmailIcon sx={{ color: '#A4A7AE' }} /></InputAdornment>) }} sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px' } }} />
                          </Box>
                          <Button startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>Add another</Button>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
                            <Button variant="text" onClick={() => setAddingMethod(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                            <Button variant="contained" sx={{ bgcolor: '#617BFF', textTransform: 'none' }}>Purchase</Button>
                          </Box>
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ border: '1px solid #E9EAEB', borderRadius: '8px', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>Visa ending in 1234</Typography>
                              <Typography sx={{ fontSize: '12px', color: '#535862' }}>Expiry 06/2025</Typography>
                              <Typography sx={{ fontSize: '12px', color: '#535862' }}>billing@flashback.tech</Typography>
                            </Box>
                            <Button variant="outlined" size="small" sx={{ textTransform: 'none', borderRadius: '8px' }}>Edit</Button>
                          </Box>
                          <Button variant="contained" onClick={() => setAddingMethod(true)} sx={{ alignSelf: 'flex-start', bgcolor: '#617BFF', textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>+ Add method</Button>
                        </>
                      )}
                    </CardContent>
                  </Card>
                  ========== END PAYMENT METHOD SECTION ========== */}
                        {/* Manage Billing via Stripe Portal */}
                        <>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: { xs: 'column', sm: 'row' },
                              justifyContent: 'space-between',
                              alignItems: { xs: 'stretch', sm: 'center' },
                              gap: { xs: 2, sm: 0 },
                              mb: 2,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: '14px', sm: '16px' },
                                lineHeight: '24px',
                                color: '#101828',
                              }}
                            >
                              Billing Management
                            </Typography>
                            <Button
                              variant="contained"
                              onClick={handleOpenBillingPortal}
                              sx={{
                                bgcolor: '#617BFF',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: { xs: '13px', sm: '14px' },
                                borderRadius: '8px',
                                width: { xs: '100%', sm: 'auto' },
                                '&:hover': { bgcolor: '#4A67E3' },
                              }}
                            >
                              Open Billing Portal
                            </Button>
                          </Box>

                          <Typography
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              fontSize: { xs: '14px', sm: '16px' },
                              lineHeight: '24px',
                              color: '#101828',
                              mt: 3,
                            }}
                          >
                            Billing history
                          </Typography>
                          <Card
                            sx={{
                              mb: 2.5,
                              borderRadius: '12px',
                              boxShadow: 'none',
                              bgcolor: 'white',
                              border: '1px solid #E9EAEB',
                              width: { xs: '100%', sm: '468px' },
                              minHeight: '260px',
                            }}
                          >
                            <CardContent sx={{ p: 0 }}>
                              <Box
                                sx={{
                                  height: '44px',
                                  bgcolor: '#FAFAFA',
                                  borderBottom: '1px solid #E9EAEB',
                                  display: 'flex',
                                  alignItems: 'center',
                                  overflowX: { xs: 'auto', sm: 'visible' },
                                }}
                              >
                                <Box
                                  sx={{ width: { xs: '120px', sm: '140px' }, minWidth: '120px' }}
                                >
                                  <Typography
                                    sx={{
                                      ml: { xs: 2, sm: 3 },
                                      fontWeight: 500,
                                      fontSize: { xs: '11px', sm: '12px' },
                                      color: '#414651',
                                    }}
                                  >
                                    Date
                                  </Typography>
                                </Box>
                                <Box sx={{ width: { xs: '80px', sm: '100px' }, minWidth: '80px' }}>
                                  <Typography
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: { xs: '11px', sm: '12px' },
                                      color: '#414651',
                                    }}
                                  >
                                    Status
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    width: { xs: '80px', sm: '100px' },
                                    minWidth: '80px',
                                    textAlign: 'right',
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: { xs: '11px', sm: '12px' },
                                      color: '#414651',
                                    }}
                                  >
                                    Amount
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    width: { xs: '100px', sm: '128px' },
                                    minWidth: '100px',
                                    textAlign: 'center',
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: { xs: '11px', sm: '12px' },
                                      color: '#414651',
                                    }}
                                  >
                                    Actions
                                  </Typography>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  minHeight: '216px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}
                              >
                                {paymentsLoading ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      py: 4,
                                    }}
                                  >
                                    <CircularProgress size={24} />
                                  </Box>
                                ) : payments.length > 0 ? (
                                  payments
                                    .slice(0, 10)
                                    .sort(
                                      (a, b) =>
                                        new Date(b.timestamp).getTime() -
                                        new Date(a.timestamp).getTime()
                                    )
                                    .map((payment, index) => {
                                      const statusConfig = formatPaymentStatus(payment.status);
                                      const isPending = payment.status === 'PENDING';
                                      return (
                                        <Box
                                          key={payment.id}
                                          sx={{
                                            height: '48px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            borderBottom:
                                              index < Math.min(payments.length - 1, 9)
                                                ? '1px solid #E9EAEB'
                                                : 'none',
                                            overflowX: { xs: 'auto', sm: 'visible' },
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: { xs: '120px', sm: '140px' },
                                              minWidth: '120px',
                                            }}
                                          >
                                            <Typography
                                              sx={{
                                                ml: { xs: 2, sm: 3 },
                                                fontSize: { xs: '12px', sm: '14px' },
                                                color: '#414651',
                                              }}
                                            >
                                              {new Date(payment.timestamp).toLocaleDateString(
                                                'en-US',
                                                { month: 'short', day: 'numeric', year: 'numeric' }
                                              )}
                                            </Typography>
                                          </Box>
                                          <Box
                                            sx={{
                                              width: { xs: '80px', sm: '100px' },
                                              minWidth: '80px',
                                            }}
                                          >
                                            <Chip
                                              label={statusConfig.label}
                                              size="small"
                                              sx={{
                                                bgcolor: statusConfig.bgcolor,
                                                color: statusConfig.color,
                                                fontSize: { xs: '10px', sm: '11px' },
                                              }}
                                            />
                                          </Box>
                                          <Box
                                            sx={{
                                              width: { xs: '80px', sm: '100px' },
                                              minWidth: '80px',
                                              textAlign: 'right',
                                              pr: { xs: 1, sm: 0 },
                                            }}
                                          >
                                            <Typography
                                              sx={{
                                                fontSize: { xs: '12px', sm: '14px' },
                                                color: '#414651',
                                              }}
                                            >
                                              {formatPrice(payment.amount, payment.currency)}
                                            </Typography>
                                          </Box>
                                          <Box
                                            sx={{
                                              width: { xs: '100px', sm: '128px' },
                                              minWidth: '100px',
                                              textAlign: 'center',
                                            }}
                                          >
                                            {isPending ? (
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  gap: 0.5,
                                                  justifyContent: 'center',
                                                }}
                                              >
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  onClick={handleNavigateToCheckout}
                                                  disabled={paymentsLoading}
                                                  sx={{
                                                    fontSize: { xs: '10px', sm: '11px' },
                                                    textTransform: 'none',
                                                    borderColor: '#617BFF',
                                                    color: '#617BFF',
                                                    borderRadius: '6px',
                                                    px: { xs: 0.5, sm: 1 },
                                                    py: 0.5,
                                                    minWidth: 'auto',
                                                    '&:hover': {
                                                      borderColor: '#4A67E3',
                                                      backgroundColor: 'rgba(97, 123, 255, 0.04)',
                                                    },
                                                  }}
                                                >
                                                  Checkout
                                                </Button>
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  onClick={handleCancelPendingPayment}
                                                  disabled={paymentsLoading}
                                                  sx={{
                                                    fontSize: { xs: '10px', sm: '11px' },
                                                    textTransform: 'none',
                                                    borderColor: '#EF4444',
                                                    color: '#EF4444',
                                                    borderRadius: '6px',
                                                    px: { xs: 0.5, sm: 1 },
                                                    py: 0.5,
                                                    minWidth: 'auto',
                                                    '&:hover': {
                                                      borderColor: '#DC2626',
                                                      backgroundColor: 'rgba(239, 68, 68, 0.04)',
                                                    },
                                                  }}
                                                >
                                                  Cancel
                                                </Button>
                                              </Box>
                                            ) : (
                                              <Typography
                                                sx={{ fontSize: '12px', color: '#717680' }}
                                              >
                                                -
                                              </Typography>
                                            )}
                                          </Box>
                                        </Box>
                                      );
                                    })
                                ) : (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      py: 4,
                                    }}
                                  >
                                    <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>
                                      No payment history available
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </>
                      </Box>
                    )}

                    {activeSection === 'Activity' && (
                      <Box sx={{ p: { xs: 2, sm: 3 }, flex: 1, overflowY: 'auto' }}>
                        {/* Filters row */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                          {/* Search */}
                          <TextField
                            size="small"
                            placeholder="Search events…"
                            value={activitySearch}
                            onChange={(e) => setActivitySearch(e.target.value)}
                            sx={{
                              flex: 1,
                              minWidth: 180,
                              '& .MuiOutlinedInput-root': {
                                height: 36,
                                borderRadius: '8px',
                                fontSize: '13px',
                              },
                            }}
                          />
                          {/* Workspace filter */}
                          <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                              value={activityWorkspaceFilter}
                              onChange={(e) => setActivityWorkspaceFilter(e.target.value)}
                              sx={{ height: 36, borderRadius: '8px', fontSize: '13px' }}
                            >
                              <MenuItem value="all">All Workspaces</MenuItem>
                              {(globalData?.workspaces || []).map((ws: any) => (
                                <MenuItem key={ws.id} value={ws.id}>{ws.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {/* User filter */}
                          <FormControl size="small" sx={{ minWidth: 130 }}>
                            <Select
                              value={activityUserFilter}
                              onChange={(e) => setActivityUserFilter(e.target.value)}
                              sx={{ height: 36, borderRadius: '8px', fontSize: '13px' }}
                            >
                              <MenuItem value="all">All Users</MenuItem>
                              {(() => {
                                const uniqueUsers = new Map<string, string>();
                                activityEvents.forEach((ev) => {
                                  if (ev.userId && ev.userName) uniqueUsers.set(ev.userId, ev.userName);
                                });
                                return Array.from(uniqueUsers.entries()).map(([uid, uname]) => (
                                  <MenuItem key={uid} value={uid}>{uname}</MenuItem>
                                ));
                              })()}
                            </Select>
                          </FormControl>
                          {/* Refresh */}
                          <IconButton size="small" onClick={loadActivityEvents} disabled={activityLoading} sx={{ color: '#6B7280' }}>
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        {/* Events list */}
                        {activityLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress size={28} sx={{ color: '#617BFF' }} />
                          </Box>
                        ) : (() => {
                          const filtered = activityEvents.filter((ev) => {
                            if (activityWorkspaceFilter !== 'all' && ev.workspaceId !== activityWorkspaceFilter) return false;
                            if (activityUserFilter !== 'all' && ev.userId !== activityUserFilter) return false;
                            if (activitySearch.trim()) {
                              const q = activitySearch.toLowerCase();
                              const ctx = ev.context?.toLowerCase() || '';
                              const evt = ev.event?.toLowerCase() || '';
                              const user = ev.userName?.toLowerCase() || '';
                              let jsonName = '';
                              try { if (ev.jsonData) { const o = JSON.parse(ev.jsonData); jsonName = (o.name || o.bucket || '').toLowerCase(); } } catch { /* noop */ }
                              if (!ctx.includes(q) && !evt.includes(q) && !user.includes(q) && !jsonName.includes(q)) return false;
                            }
                            return true;
                          });

                          if (filtered.length === 0) {
                            return (
                              <Box sx={{ textAlign: 'center', py: 6 }}>
                                <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>No events found</Typography>
                              </Box>
                            );
                          }

                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                              {filtered.map((ev) => {
                                const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
                                let nameOrBucket = '';
                                try { if (ev.jsonData) { const o = JSON.parse(ev.jsonData); nameOrBucket = o.name || o.bucket || ''; } } catch { /* noop */ }
                                const ctx = ev.context?.toLowerCase() || '';
                                const evt = ev.event?.toLowerCase() || '';
                                let action = '';
                                if (evt === 'created') action = 'successfully created';
                                else if (evt === 'updated') action = ctx === 'usersettings' ? 'were changed' : 'updated';
                                else if (evt === 'deleted') action = 'deleted';
                                else action = evt;
                                let typeLabel = '';
                                if (ctx === 'bucket') typeLabel = 'Bucket';
                                else if (ctx === 'workspace') typeLabel = 'Workspace';
                                else if (ctx === 'repo') typeLabel = 'Repository';
                                else if (ctx === 'usersettings') typeLabel = 'User settings';
                                else if (ctx === 'apikey') typeLabel = 'API Key';
                                else typeLabel = cap(ctx);

                                const wsName = (globalData?.workspaces || []).find((w: any) => w.id === ev.workspaceId)?.name || '';

                                return (
                                  <Box
                                    key={ev.id}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: 2,
                                      py: 1.5,
                                      px: 1,
                                      borderBottom: '1px solid #F2F4F7',
                                      borderLeft: ev.showUnread ? '3px solid #617BFF' : '3px solid transparent',
                                      bgcolor: ev.showUnread ? 'rgba(97,123,255,0.04)' : 'transparent',
                                      '&:hover': { bgcolor: '#FAFAFA' },
                                    }}
                                  >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#111827', textTransform: 'capitalize' }}>
                                        {ev.context} · {ev.event}
                                      </Typography>
                                      <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.25 }}>
                                        {nameOrBucket ? `${typeLabel} "${nameOrBucket}" ${action}` : `${typeLabel} ${action}`}
                                      </Typography>
                                      {wsName && (
                                        <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>
                                          Workspace: {wsName}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
                                      <Typography sx={{ fontSize: '12px', color: '#6B7280', whiteSpace: 'nowrap' }}>
                                        {new Date(ev.timestamp).toLocaleString()}
                                      </Typography>
                                      {ev.userId !== currentUserId && ev.userName && (
                                        <Typography sx={{ fontSize: '11px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                                          By: {ev.userName}
                                        </Typography>
                                      )}
                                      {ev.showUnread ? (
                                        <Typography sx={{ fontSize: '11px', color: '#617BFF', fontWeight: 500 }}>New</Typography>
                                      ) : (
                                        <Typography sx={{ fontSize: '11px', color: '#22C55E' }}>✓ Read</Typography>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                        })()}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>

          {/* Footer for Organization tab - always at bottom of modal */}
          {activeSection === 'Details' && activeTab === 'Organization' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'flex-end',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 2, sm: 0 },
                borderTop: '1px solid #E9EAEB',
                bgcolor: 'white',
                px: { xs: 2, sm: 3 },
                py: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    // Reset to original values
                    setCompanyName(org?.name || '');
                    setCompanyAddress1(org?.address1 || '');
                    setCompanyDomain(org?.domain || '');
                  }}
                  sx={{
                    borderColor: '#E9EAEB',
                    color: '#414651',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '13px', sm: '14px' },
                    borderRadius: '8px',
                    height: '36px',
                    flex: { xs: 1, sm: 'none' },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveCompany}
                  disabled={!canEditCompany || companyLoading || !companyName.trim() || !companyDomain.trim() || !companyAddress1.trim() || !companyCity.trim() || !companyCountry.trim() || !companyWebsite.trim()}
                  sx={{
                    bgcolor: (canEditCompany && !companyLoading && companyName.trim() && companyDomain.trim() && companyAddress1.trim() && companyCity.trim() && companyCountry.trim() && companyWebsite.trim()) ? '#617BFF' : '#E5E7EB',
                    color: (canEditCompany && !companyLoading && companyName.trim() && companyDomain.trim() && companyAddress1.trim() && companyCity.trim() && companyCountry.trim() && companyWebsite.trim()) ? 'white' : '#9CA3AF',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '13px', sm: '14px' },
                    borderRadius: '8px',
                    height: '36px',
                    flex: { xs: 1, sm: 'none' },
                    '&:hover': {
                      bgcolor: canEditCompany && !companyLoading ? '#4A67E3' : '#E5E7EB',
                    },
                    '&.Mui-disabled': {
                      bgcolor: '#E5E7EB',
                      color: '#9CA3AF',
                    },
                  }}
                >
                  {companyLoading ? 'Saving...' : 'Save changes'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Footer for Workspace tab - always at bottom of modal */}
          {activeSection === 'Details' && activeTab === 'Workspace' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'flex-end',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 2, sm: 0 },
                borderTop: '1px solid #E9EAEB',
                bgcolor: 'white',
                px: { xs: 2, sm: 3 },
                py: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (workspaceHasChanges) {
                      setWorkspaceWarnDialog(true);
                      setWorkspacePendingAction(() => () => {
                        setWorkspaceHasChanges(false);
                        // Reset workspace changes here
                      });
                    }
                  }}
                  disabled={!workspaceHasChanges}
                  sx={{
                    borderColor: '#E9EAEB',
                    color: '#414651',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '13px', sm: '14px' },
                    borderRadius: '8px',
                    height: '36px',
                    flex: { xs: 1, sm: 'none' },
                    '&.Mui-disabled': {
                      borderColor: '#E9EAEB',
                      color: '#9CA3AF',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    // Save workspace changes
                    setWorkspaceHasChanges(false);
                  }}
                  disabled={!workspaceHasChanges}
                  sx={{
                    bgcolor: workspaceHasChanges ? '#617BFF' : '#E5E7EB',
                    color: workspaceHasChanges ? 'white' : '#9CA3AF',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '13px', sm: '14px' },
                    borderRadius: '8px',
                    height: '36px',
                    flex: { xs: 1, sm: 'none' },
                    '&:hover': {
                      bgcolor: workspaceHasChanges ? '#4A67E3' : '#E5E7EB',
                    },
                    '&.Mui-disabled': {
                      bgcolor: '#E5E7EB',
                      color: '#9CA3AF',
                    },
                  }}
                >
                  Save changes
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Workspace unsaved changes warning dialog */}
      <Dialog
        open={workspaceWarnDialog}
        onClose={() => setWorkspaceWarnDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '18px', color: '#101828' }}>
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#475467', fontSize: '14px' }}>
            You have unsaved modifications in your workspace. Do you want to discard these changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setWorkspaceWarnDialog(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderColor: '#E9EAEB',
              color: '#414651',
              '&:hover': {
                borderColor: '#D1D5DB',
                bgcolor: '#F9FAFB',
              },
            }}
          >
            Keep editing
          </Button>
          <Button
            onClick={() => {
              if (workspacePendingAction) {
                workspacePendingAction();
                setWorkspacePendingAction(null);
              }
              setWorkspaceWarnDialog(false);
            }}
            variant="contained"
            sx={{
              textTransform: 'none',
              bgcolor: '#F04438',
              '&:hover': {
                bgcolor: '#D92D20',
              },
            }}
          >
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>
      {/* Modals used by Plan tab actions */}
      <UpgradePlanModal
        open={upgradePlanModalOpen}
        onClose={() => setUpgradePlanModalOpen(false)}
        currentPlan={userQuota?.subscription?.name || quotaData?.subscriptionName || currentSubscription?.name}
      />
      <PayAsYouNeedModal open={payAsYouNeedOpen} onClose={() => setPayAsYouNeedOpen(false)} />
    </>
  );
};

export default OrganizationModal;
